import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, ScrollText, Plus, Users, ClipboardList, Coins, ShoppingCart,
  Pencil, Trash2, Briefcase, User, Clock, BarChart3, Calendar, Store, Filter, AlertTriangle, Calculator, Settings, Repeat, TrendingUp
} from 'lucide-react';
import LogModal from '../components/LogModal';
import useEscapeClose from '../hooks/useEscapeClose';
import logService from '../services/logService';
import { API_URL, API_HOST } from '../config/api';
import PriceInput from '../components/PriceInput';
import './PersonnelManagement.css';
import PersonnelMobileHome from './PersonnelMobileHome';

// Satışın görüntülenecek tutarını hesapla (RetailSales ile aynı mantık)
const getSalePaidAmount = (sale) => {
  const total = Number(sale.total) || 0;
  const subtotal = Number(sale.subtotal) || 0;
  const discount = Number(sale.discount) || 0;
  const scrapAmount = Number(sale.scrap_total_amount) || 0;
  const scrapDebt = Number(sale.scrap_debt_discount) || 0;
  if (discount > 0 && subtotal > 0 && Math.abs(total - subtotal) < 1) {
    return total - discount - scrapDebt;
  }
  return total + scrapAmount;
};

// Çoklu kasiyer eşleştirme. staff_name'i "&" ve "," ile parçalar,
// her parçayı gerçek personel adlarıyla karşılaştırır.
// Dönen liste: bu satışı paylaşan tanınan personel adları.
// 0 veya 1 eşleşme → tek kasiyer (paylaşım yok), ≥2 → paylaşım var.
const matchCashiers = (staffName, employeeNames) => {
  if (!staffName) return [];
  const tokens = staffName.split(/\s*[&,]\s*/).map(t => t.trim()).filter(Boolean);
  const lower = employeeNames.map(n => (n || '').toLowerCase().trim()).filter(Boolean);
  const matched = [];
  for (const tok of tokens) {
    const tl = tok.toLowerCase();
    const hit = lower.find(en => en === tl || en.includes(tl) || tl.includes(en));
    if (hit && !matched.includes(hit)) matched.push(hit);
  }
  return matched;
};

// mobileApp: iOS uygulamasının WebView'ı (/mobile/personel, MobilePersonel.js) — ana görünüm telefona göre
// PersonnelMobileHome ile çizilir (personel kartları, aynı satır hesapları: getEmployeeRow); Formüller sekmesi
// ve tüm modallar (personel, maaş detay, yoklama, ek mesai, satış geçmişi) web ile ortaktır.
const PersonnelManagement = ({ user, selectedStore, mobileApp = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isExchangeRoute = /\/degisimfisi\/?$/.test(location.pathname);
  const [activeTab, setActiveTab] = useState(isExchangeRoute ? 'exchanges' : 'employees');
  const [loading, setLoading] = useState(true);

  // Personel state'leri
  const [employees, setEmployees] = useState([]);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [originalEmployee, setOriginalEmployee] = useState(null);
  const [stores, setStores] = useState([]);
  const [showLogModal, setShowLogModal] = useState(false);

  // Sütun ayarları state'leri
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  const defaultEmployeeColumns = {
    personnel: true,
    monthlyAbsent: true,
    totalAbsent: true,
    salary: true,
    deduction: true,
    overtime: false,
    sales: false,
    grossSales: true,
    refundsTotal: true,
    bonusEligible: true,
    bonus: true,
    bonusDebt: true,
    total: true,
    remainingLeave: false,
    bonusFormula: false,
    weeklyHours: false,
    monthlyHours: false,
    actions: true
  };

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('personnelVisibleColumns');
    if (saved) return { ...defaultEmployeeColumns, ...JSON.parse(saved) };
    return defaultEmployeeColumns;
  });

  const allEmployeeColumns = [
    { key: 'personnel', label: 'Personel' },
    { key: 'monthlyAbsent', label: 'Devamsızlık (Ay)' },
    { key: 'totalAbsent', label: 'Devamsızlık (Top.)' },
    { key: 'remainingLeave', label: 'Kalan İzin' },
    { key: 'weeklyHours', label: 'Haftalık Saat' },
    { key: 'monthlyHours', label: 'Aylık Saat' },
    { key: 'salary', label: 'Maaş' },
    { key: 'deduction', label: 'Kesinti' },
    { key: 'overtime', label: 'Ek Mesai' },
    { key: 'sales', label: 'Satış' },
    { key: 'grossSales', label: 'Toplam Satış' },
    { key: 'refundsTotal', label: 'Toplam İade' },
    { key: 'bonusEligible', label: 'Net Satış' },
    { key: 'bonusFormula', label: 'Prim Formülü' },
    { key: 'bonus', label: 'Prim' },
    { key: 'bonusDebt', label: 'Prim Ödemesi' },
    { key: 'total', label: 'Toplam' },
    { key: 'actions', label: 'İşlem' }
  ];

  // Sekme bazlı kolon preset'leri (devam, maaş, prim sekmeleri için sabit)
  const allColumnKeys = allEmployeeColumns.map(c => c.key);
  const buildPreset = (keys) => allColumnKeys.reduce((acc, k) => ({ ...acc, [k]: keys.includes(k) }), {});
  const tabColumnPresets = {
    attendance: buildPreset(['personnel', 'monthlyAbsent', 'totalAbsent', 'remainingLeave', 'weeklyHours', 'monthlyHours', 'actions']),
    salary: buildPreset(['personnel', 'monthlyHours', 'salary', 'deduction', 'overtime', 'total', 'actions']),
    bonus: buildPreset(['personnel', 'grossSales', 'refundsTotal', 'bonusEligible', 'bonusFormula', 'bonus', 'bonusDebt', 'actions'])
  };

  const toggleColumn = (columnKey) => {
    const newColumns = { ...visibleColumns, [columnKey]: !visibleColumns[columnKey] };
    setVisibleColumns(newColumns);
    localStorage.setItem('personnelVisibleColumns', JSON.stringify(newColumns));
  };

  const resetColumns = () => {
    setVisibleColumns(defaultEmployeeColumns);
    localStorage.setItem('personnelVisibleColumns', JSON.stringify(defaultEmployeeColumns));
  };

  // Yoklama state'leri
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedAttendanceEmployee, setSelectedAttendanceEmployee] = useState(null);
  const [attendanceForm, setAttendanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    status: 'unpaid_leave',
    reason: '',
    partial_hours: ''
  });
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [selectedOvertimeEmployee, setSelectedOvertimeEmployee] = useState(null);
  const [overtimeForm, setOvertimeForm] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: '',
    notes: ''
  });
  const [overtimeHistory, setOvertimeHistory] = useState([]);

  // Personel düzenleme modalı için izin geçmişi (ücretli/ücretsiz günler)
  const [employeeLeaveHistory, setEmployeeLeaveHistory] = useState({ paid: [], unpaid: [], loading: false });

  // İzin state'leri
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [newLeaveRequest, setNewLeaveRequest] = useState({
    employee_id: '', leave_type: 'annual', start_date: '', end_date: '', days: 0, reason: ''
  });

  // Maaş state'leri
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [salaryData, setSalaryData] = useState([]);
  const [showSalaryDetailModal, setShowSalaryDetailModal] = useState(false);
  const [selectedEmployeeSalary, setSelectedEmployeeSalary] = useState(null);

  // Satış geçmişi state'leri
  const [showSalesHistoryModal, setShowSalesHistoryModal] = useState(false);
  const [salesHistoryEmployee, setSalesHistoryEmployee] = useState(null);
  const [salesHistoryData, setSalesHistoryData] = useState([]);
  const [loadingSalesHistory, setLoadingSalesHistory] = useState(false);

  // Görüntülenen ayın prim ödeme durumu — Maaş Detay modal'ı içinde toggle
  const [currentBonusStatus, setCurrentBonusStatus] = useState({ paid: false, paid_at: null, notes: '', loading: false });
  const [bonusNotesDraft, setBonusNotesDraft] = useState('');

  // Görüntülenen ayın maaş ödeme durumu — Maaş Detay modal'ı içinde toggle
  const [currentSalaryStatus, setCurrentSalaryStatus] = useState({ paid: false, paid_at: null, notes: '', loading: false });
  const [salaryNotesDraft, setSalaryNotesDraft] = useState('');

  // Vadesi geçmiş prim borcu (bugünden 2 ay önceki ay ödenmemişse) — personel index'lenmiş array
  const [overdueBonus, setOverdueBonus] = useState([]);

  // Modal kapanış animasyonu için state
  const [closingModal, setClosingModal] = useState(null);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Modal kapanış handler'ı
  const handleCloseModal = (modalName, setShowModal) => {
    setClosingModal(modalName);
    setTimeout(() => {
      setShowModal(false);
      setClosingModal(null);
    }, 250);
  };

  // Satış Sorumluları state'leri
  const [salesStaffList, setSalesStaffList] = useState([]);
  const [salesHistory, setSalesHistory] = useState([]);
  const [loadingSalesData, setLoadingSalesData] = useState(false);
  const [salesStaffSalaryMap, setSalesStaffSalaryMap] = useState({});

  // Masraflar bölümü PaymentCalendar (Ödeme Takip Sistemi) sayfasına taşındı

  // Formüller state'leri
  const [bonusFormulas, setBonusFormulas] = useState([]);
  const [loadingFormulas, setLoadingFormulas] = useState(false);
  const [dirtyFormulaIds, setDirtyFormulaIds] = useState(new Set());
  const [savingFormulas, setSavingFormulas] = useState(false);

  // Değişim sekmesi state'leri
  const [exchangeReceipts, setExchangeReceipts] = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(false);
  const [exchangeMonth, setExchangeMonth] = useState(new Date().getMonth() + 1);
  const [exchangeYear, setExchangeYear] = useState(new Date().getFullYear());
  const [exchangeStoreFilter, setExchangeStoreFilter] = useState('all');
  const markFormulaDirty = (id) => setDirtyFormulaIds(prev => {
    const next = new Set(prev);
    next.add(id);
    return next;
  });

  const defaultWorkSchedule = {
    monday: { active: true, start: '09:00', end: '18:00' },
    tuesday: { active: true, start: '09:00', end: '18:00' },
    wednesday: { active: true, start: '09:00', end: '18:00' },
    thursday: { active: true, start: '09:00', end: '18:00' },
    friday: { active: true, start: '09:00', end: '18:00' },
    saturday: { active: true, start: '10:00', end: '17:00' },
    sunday: { active: false, start: '', end: '' }
  };

  // Yeni/Düzenlenen personel form state'i
  const [employeeForm, setEmployeeForm] = useState({
    name: '', phone: '', email: '', tc_no: '', position: 'Satış Sorumlusu',
    store_ids: [], hire_date: '', birth_date: '', salary: '', salary_type: 'monthly',
    hourly_rate: '', overtime_multiplier: '1.5', daily_work_hours: '', work_hours_value: '', work_hours_unit: 'daily', monthly_salary_30: '', work_schedule: defaultWorkSchedule,
    annual_leave_days: 14, commission_rate: '', bonus_formula_set_id: '', fixed_bonus: '', is_sales_staff: true, notes: ''
  });

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showColumnSettings, () => setShowColumnSettings(false)],
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showSalesHistoryModal, () => setShowSalesHistoryModal(false)],
    [showSalaryDetailModal, () => setShowSalaryDetailModal(false)],
    [showAttendanceModal, () => setShowAttendanceModal(false)],
    [showOvertimeModal, () => setShowOvertimeModal(false)],
    [showLeaveModal, () => setShowLeaveModal(false)],
    [showEmployeeModal, () => setShowEmployeeModal(false)],
    [showLogModal, () => {}],
  ]);

  // Sayfa yüklendiğinde verileri getir
  useEffect(() => {
    fetchEmployees();
    fetchStores();
    fetchBonusFormulas();
  }, []);

  // Tab değiştiğinde ilgili verileri yükle
  useEffect(() => {
    if (activeTab === 'employees' || activeTab === 'attendance' || activeTab === 'salary' || activeTab === 'bonus') {
      fetchAttendance();
      if (employees.length > 0) {
        fetchSalaryData();
        fetchOverdueBonus();
      }
    } else if (activeTab === 'salesStaff') {
      fetchSalesStaffData();
    } else if (activeTab === 'formulas') {
      fetchBonusFormulas();
    } else if (activeTab === 'exchanges') {
      fetchExchangeReceipts();
    }
  }, [activeTab, attendanceDate, selectedMonth, selectedYear, exchangeMonth, exchangeYear, exchangeStoreFilter, employees.length]);

  // Değişim fişlerini getir (seçilen ay aralığı)
  const fetchExchangeReceipts = async () => {
    try {
      setLoadingExchanges(true);
      const startDate = `${exchangeYear}-${String(exchangeMonth).padStart(2, '0')}-01`;
      const lastDay = new Date(exchangeYear, exchangeMonth, 0).getDate();
      const endDate = `${exchangeYear}-${String(exchangeMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const params = new URLSearchParams();
      params.set('start_date', startDate);
      params.set('end_date', endDate);
      if (exchangeStoreFilter && exchangeStoreFilter !== 'all') params.set('store_id', exchangeStoreFilter);
      const response = await fetch(`${API_URL}/exchange-receipts?${params.toString()}`);
      const data = await response.json();
      setExchangeReceipts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Değişim fişleri yükleme hatası:', error);
      setExchangeReceipts([]);
    } finally {
      setLoadingExchanges(false);
    }
  };

  // Mağazaları getir
  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_URL}/stores`);
      const data = await response.json();
      setStores(data);
    } catch (error) {
      console.error('Mağaza yükleme hatası:', error);
    }
  };

  // Personelleri getir
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/employees`);
      const data = await response.json();
      // Array olduğundan emin ol
      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Personel yükleme hatası:', error);
      toast.error('Personeller yüklenemedi!');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // Yoklama verilerini getir
  const fetchAttendance = async () => {
    try {
      setLoadingAttendance(true);
      const response = await fetch(`${API_URL}/attendance?date=${attendanceDate}`);
      const data = await response.json();
      setAttendanceData(data.attendance || []);
    } catch (error) {
      console.error('Yoklama yükleme hatası:', error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  // Belirli personelin yoklama geçmişini getir
  const fetchEmployeeAttendanceHistory = async (employeeId) => {
    try {
      const response = await fetch(`${API_URL}/attendance/history/${employeeId}`);
      const data = await response.json();
      setAttendanceHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Yoklama geçmişi hatası:', error);
      setAttendanceHistory([]);
    }
  };

  // Geçmişe yönelik yoklama kaydet
  const handleSaveAttendanceRecord = async () => {
    if (!selectedAttendanceEmployee || !attendanceForm.date) {
      toast.warning('Tarih seçmelisiniz!');
      return;
    }

    const statusNames = {
      present: 'Geldi', absent: 'Gelmedi', late: 'Geç Kaldı',
      half_day: 'Yarım Gün', paid_leave: 'Ücretli İzin', unpaid_leave: 'Ücretsiz İzin', sick_leave: 'Raporlu',
      hourly_absence: 'Saatlik Devamsızlık'
    };

    if (attendanceForm.status === 'hourly_absence' && (!attendanceForm.partial_hours || parseFloat(attendanceForm.partial_hours) <= 0)) {
      toast.warning('Saatlik devamsızlık için saat girmelisiniz!');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: selectedAttendanceEmployee.id,
          date: attendanceForm.date,
          status: attendanceForm.status,
          notes: attendanceForm.reason,
          partial_hours: attendanceForm.status === 'hourly_absence' ? attendanceForm.partial_hours : null,
          created_by: user?.id
        })
      });

      if (response.ok) {
        logService.log('personnel', 'create',
          `Yoklama kaydı eklendi: ${selectedAttendanceEmployee.name} - ${attendanceForm.date} - ${statusNames[attendanceForm.status]}`, {
          entity_type: 'attendance',
          entity_id: selectedAttendanceEmployee.id,
          details: {
            personel: selectedAttendanceEmployee.name,
            tarih: attendanceForm.date,
            durum: statusNames[attendanceForm.status],
            sebep: attendanceForm.reason || '-',
            kaydeden: user?.name || '-'
          },
          status: 'success'
        });

        toast.success('Yoklama kaydedildi!');
        setShowAttendanceModal(false);
        setAttendanceForm({ date: new Date().toISOString().split('T')[0], status: 'unpaid_leave', reason: '', partial_hours: '' });
        fetchAttendance();
        fetchEmployeeAttendanceHistory(selectedAttendanceEmployee.id);
        fetchSalaryData();
      } else {
        toast.error('Kayıt başarısız!');
      }
    } catch (error) {
      console.error('Yoklama kaydetme hatası:', error);
      toast.error('Kayıt başarısız!');
    }
  };

  // Yoklama kaydı modalını aç
  const openAttendanceModal = (employee, isToday = false) => {
    setSelectedAttendanceEmployee(employee);
    setAttendanceForm({
      date: new Date().toISOString().split('T')[0],
      status: 'unpaid_leave',
      reason: isToday ? '' : ''
    });
    fetchEmployeeAttendanceHistory(employee.id);
    setShowAttendanceModal(true);
  };

  // Bugün gelmedi olarak hızlı işaretle (ücretsiz izin olarak kaydedilir — maaştan kesilir)
  const markAbsentToday = async (employee) => {
    const today = new Date().toISOString().split('T')[0];

    try {
      const response = await fetch(`${API_URL}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: employee.id,
          date: today,
          status: 'unpaid_leave',
          notes: '',
          created_by: user?.id
        })
      });

      if (response.ok) {
        toast.success(`${employee.name} bugün için ücretsiz izinli (devamsız) olarak işaretlendi`);
        // Log kaydet
        logService.log('personnel', 'create', `Ücretsiz izin kaydı: ${employee.name} - ${today}`, {
          entity_type: 'attendance',
          entity_id: employee.id,
          status: 'info',
          details: {
            employee_name: employee.name,
            date: today,
            status: 'unpaid_leave'
          }
        });
        fetchAttendance();
      } else {
        toast.error('İşlem başarısız!');
      }
    } catch (error) {
      console.error('Devamsızlık kaydetme hatası:', error);
      toast.error('İşlem başarısız!');
      logService.log('personnel', 'create', `Devamsızlık kayıt hatası: ${employee.name}`, {
        status: 'error',
        details: { error: error.message }
      });
    }
  };

  // Ek mesai modalını aç
  const openOvertimeModal = (employee) => {
    setSelectedOvertimeEmployee(employee);
    setOvertimeForm({
      date: new Date().toISOString().split('T')[0],
      hours: '',
      notes: ''
    });
    fetchOvertimeHistory(employee.id);
    setShowOvertimeModal(true);
  };

  // Ek mesai geçmişini getir
  const fetchOvertimeHistory = async (employeeId) => {
    try {
      const response = await fetch(`${API_URL}/overtime/history/${employeeId}`);
      const data = await response.json();
      setOvertimeHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ek mesai geçmişi hatası:', error);
      setOvertimeHistory([]);
    }
  };

  // Ek mesai kaydet
  const handleSaveOvertime = async () => {
    if (!selectedOvertimeEmployee || !overtimeForm.date || !overtimeForm.hours) {
      toast.warning('Tarih ve saat alanlarını doldurun!');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/overtime`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: selectedOvertimeEmployee.id,
          date: overtimeForm.date,
          hours: parseFloat(overtimeForm.hours),
          notes: overtimeForm.notes,
          created_by: user?.id
        })
      });

      if (response.ok) {
        toast.success('Ek mesai kaydedildi!');
        setOvertimeForm({ date: new Date().toISOString().split('T')[0], hours: '', notes: '' });
        fetchOvertimeHistory(selectedOvertimeEmployee.id);
        fetchAttendance();
      } else {
        toast.error('Kayıt başarısız!');
      }
    } catch (error) {
      console.error('Ek mesai kaydetme hatası:', error);
      toast.error('Kayıt başarısız!');
    }
  };

  // Yoklama kaydı sil
  const handleDeleteAttendance = (record) => {
    const dateLabel = new Date(record.date).toLocaleDateString('tr-TR');
    showConfirmation({
      title: 'Yoklama Kaydını Sil',
      message: `${dateLabel} tarihli yoklama kaydı silinsin mi?`,
      confirmText: 'Sil',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/attendance/${record.id}`, { method: 'DELETE' });
          if (response.ok) {
            toast.success('Yoklama kaydı silindi');
            if (selectedAttendanceEmployee) {
              fetchEmployeeAttendanceHistory(selectedAttendanceEmployee.id);
            }
            fetchAttendance();
            fetchSalaryData();
            logService.log('personnel', 'delete', `Yoklama kaydı silindi: ${selectedAttendanceEmployee?.name || '-'} - ${dateLabel}`, {
              entity_type: 'attendance',
              entity_id: record.id,
              status: 'warning'
            });
          } else {
            toast.error('Silme başarısız!');
          }
        } catch (error) {
          console.error('Yoklama silme hatası:', error);
          toast.error('Silme başarısız!');
        }
      }
    });
  };

  // Ek mesai kaydı sil
  const handleDeleteOvertime = (record) => {
    const dateLabel = new Date(record.date).toLocaleDateString('tr-TR');
    showConfirmation({
      title: 'Ek Mesai Kaydını Sil',
      message: `${dateLabel} tarihli ${record.hours} saatlik ek mesai kaydı silinsin mi?`,
      confirmText: 'Sil',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/overtime/${record.id}`, { method: 'DELETE' });
          if (response.ok) {
            toast.success('Ek mesai kaydı silindi');
            if (selectedOvertimeEmployee) {
              fetchOvertimeHistory(selectedOvertimeEmployee.id);
            }
            fetchAttendance();
            logService.log('personnel', 'delete', `Ek mesai kaydı silindi: ${selectedOvertimeEmployee?.name || '-'} - ${dateLabel} (${record.hours} saat)`, {
              entity_type: 'overtime',
              entity_id: record.id,
              status: 'warning'
            });
          } else {
            toast.error('Silme başarısız!');
          }
        } catch (error) {
          console.error('Ek mesai silme hatası:', error);
          toast.error('Silme başarısız!');
        }
      }
    });
  };

  // İzin taleplerini getir
  const fetchLeaveRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/leave-requests`);
      const data = await response.json();
      setLeaveRequests(data);
    } catch (error) {
      console.error('İzin talepleri hatası:', error);
    }
  };

  // Maaş verilerini getir
  const fetchSalaryData = async () => {
    try {
      const salaries = await Promise.all(
        employees.map(async (emp) => {
          const response = await fetch(
            `${API_URL}/employees/${emp.id}/salary?month=${selectedMonth}&year=${selectedYear}`
          );
          return response.json();
        })
      );
      setSalaryData(salaries);
    } catch (error) {
      console.error('Maaş verileri hatası:', error);
    }
  };

  // Prim borcu: bugünden 2 ay önceki ay için prim hesapla, ödeme durumunu kontrol et
  // Örn: bugün 1 Mayıs ise → Mart ayı primi vadesi geçmiş kabul edilir.
  const fetchOverdueBonus = async () => {
    try {
      const now = new Date();
      let dueMonth = now.getMonth() + 1 - 2; // 2 ay önce
      let dueYear = now.getFullYear();
      if (dueMonth <= 0) { dueMonth += 12; dueYear -= 1; }

      const results = await Promise.all(
        employees.map(async (emp) => {
          try {
            const [salResp, statResp] = await Promise.all([
              fetch(`${API_URL}/employees/${emp.id}/salary?month=${dueMonth}&year=${dueYear}`),
              fetch(`${API_URL}/employees/${emp.id}/bonus-payments?year=${dueYear}`)
            ]);
            const sal = await salResp.json();
            const statList = await statResp.json();
            const totalBonus = (Number(sal.commission_amount) || 0) + (Number(sal.formula_bonus_amount) || 0);
            const stat = (Array.isArray(statList) ? statList : []).find(s => s.period_month === dueMonth);
            const isPaid = !!stat?.paid;
            if (totalBonus > 0.01 && !isPaid) {
              return { amount: totalBonus, period_month: dueMonth, period_year: dueYear };
            }
            return null;
          } catch (_) {
            return null;
          }
        })
      );
      setOverdueBonus(results);
    } catch (error) {
      console.error('Prim borcu hesaplama hatası:', error);
      setOverdueBonus([]);
    }
  };

  // Satış sorumluları ve satış verilerini getir
  const fetchSalesStaffData = async () => {
    try {
      setLoadingSalesData(true);

      // Geçmiş satışları getir
      const salesResponse = await fetch(`${API_URL}/sales?limit=100`);
      const salesData = await salesResponse.json();
      setSalesHistory(salesData);

      // is_sales_staff=true olan personelleri filtrele
      const salesStaff = employees.filter(emp => emp.is_sales_staff);
      setSalesStaffList(salesStaff);

      // Her satış sorumlusu için maaş/prim bilgisini çek
      const salaryMap = {};
      await Promise.all(salesStaff.map(async (emp) => {
        try {
          const res = await fetch(`${API_URL}/employees/${emp.id}/salary?month=${selectedMonth}&year=${selectedYear}`);
          const data = await res.json();
          salaryMap[emp.id] = data;
        } catch (e) {}
      }));
      setSalesStaffSalaryMap(salaryMap);
    } catch (error) {
      console.error('Satış verileri hatası:', error);
    } finally {
      setLoadingSalesData(false);
    }
  };

  // Personelin satış geçmişini getir
  const viewSalesHistory = async (employee) => {
    setSalesHistoryEmployee(employee);
    setShowSalesHistoryModal(true);
    setLoadingSalesHistory(true);
    try {
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2,'0')}-01`;
      const endDate = `${selectedYear}-${String(selectedMonth).padStart(2,'0')}-${new Date(selectedYear, selectedMonth, 0).getDate()}`;
      // Satışları ve değişim fişlerini paralel çek
      const [salesResp, exchResp] = await Promise.all([
        fetch(`${API_URL}/sales?all=true&start_date=${startDate}&end_date=${endDate}`),
        fetch(`${API_URL}/exchange-receipts?start_date=${startDate}&end_date=${endDate}`)
      ]);
      const allSales = await salesResp.json();
      const exchanges = await exchResp.json();

      // Değişim eşleşme haritaları
      const exchangeByNewSaleId = new Map();
      const refundSaleIdsInExchange = new Set();
      (Array.isArray(exchanges) ? exchanges : []).forEach(ex => {
        if (ex.new_sale_id) exchangeByNewSaleId.set(Number(ex.new_sale_id), ex);
        if (ex.matched_refund_sale_id) refundSaleIdsInExchange.add(Number(ex.matched_refund_sale_id));
      });

      // Çoklu kasiyer paylaşımı için: tüm gerçek personel adları
      const allEmpNames = (employees || []).map(e => e.name).filter(Boolean);

      // Personelin satışlarını filtrele
      // staff_name esas alınır (staff_id bazen eski/yeniden atanmış sales_staff
      // id'lerine işaret edebiliyor ve yanlış personele bağlanabiliyor).
      // staff_name yoksa ancak o zaman staff_id'ye düş.
      const empName = employee.name.toLowerCase().trim();
      const empSales = (Array.isArray(allSales) ? allSales : []).filter(s => {
        if (s.staff_name) {
          const matched = matchCashiers(s.staff_name, allEmpNames);
          return matched.includes(empName);
        }
        if (s.staff_id === employee.sales_staff_id && employee.sales_staff_id) return true;
        if (s.staff_id === employee.id) return true;
        return false;
      });

      // Değişim işlemi olan satışlar artık iki ayrı satır olarak gösterilir
      // (iade + yeni satış); fark hesabı yapılmaz. Toplam matematiksel olarak
      // değişmediği için (iade + yeni = fark) prim hesabı aynı kalır.
      // Sadece kullanıcıya görsel ipucu için _exchangeId etiketi eklenir.
      const transformed = [];
      for (const s of empSales) {
        const sid = Number(s.id);
        const entry = { ...s };
        // Değişim parçası mı? (cosmetic etiket için)
        const ex = exchangeByNewSaleId.get(sid);
        if (ex) {
          entry._exchangeId = ex.id;
          entry._exchangeRole = 'new'; // yeni satış kısmı
        } else if (refundSaleIdsInExchange.has(sid)) {
          entry._exchangeRole = 'refund'; // değişimin iade kısmı
        }
        // Çoklu kasiyer tespiti (orijinal staff_name üzerinden)
        const matchedCashiers = matchCashiers(s.staff_name, allEmpNames);
        const cashierCount = Math.max(1, matchedCashiers.length);
        entry._cashierCount = cashierCount;
        entry._isShared = cashierCount > 1;
        transformed.push(entry);
      }

      // Değişimin iade tarafı `sales` tablosuna hiç yazılmıyor (backend kasıtlı
      // olarak sadece `exchanges` tablosuna kayıt atıyor), bu yüzden empSales
      // içinde hiçbir zaman görünmez. Backend'in prim hesabı (/api/employees/:id/salary)
      // bu iadeyi exchanges.return_total üzerinden düşüyor; burada da aynı mantıkla
      // sentetik bir "iade" satırı ekleyip toplamı backend ile birebir eşleştiriyoruz.
      // Atıf backend'deki gibi ORİJİNAL satışın personeline göre yapılır (işlemi
      // yapan operatöre değil), ve zaten matched_refund_sale_id ile gerçek bir
      // sales satırı bulunmuşsa (legacy/gelecek durum) tekrar eklenmez.
      (Array.isArray(exchanges) ? exchanges : []).forEach(ex => {
        if (ex.matched_refund_sale_id) return; // zaten empSales içinde bir satırla temsil ediliyor
        if (ex.original_sale_status === 'refunded') return; // backend de bu durumda düşmüyor
        const matchedCashiers = matchCashiers(ex.original_staff_name, allEmpNames);
        if (!matchedCashiers.includes(empName)) return;
        const cashierCount = Math.max(1, matchedCashiers.length);
        transformed.push({
          id: `exchange-refund-${ex.exchange_id}`,
          created_at: ex.exchange_created_at,
          receipt_number: ex.return_receipt_number,
          customer_name: ex.customer_name || ex.exchange_customer_name,
          payment_method: ex.payment_method,
          staff_name: ex.original_staff_name,
          status: 'completed',
          sale_type: 'refund',
          total: -(Number(ex.return_total) || 0),
          subtotal: 0,
          discount: 0,
          scrap_total_amount: 0,
          scrap_debt_discount: 0,
          _exchangeId: ex.exchange_id,
          _exchangeRole: 'refund',
          _cashierCount: cashierCount,
          _isShared: cashierCount > 1
        });
      });

      // Tarihe göre sırala (yeniden yeniye)
      transformed.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setSalesHistoryData(transformed);
    } catch (error) {
      console.error('Satış geçmişi hatası:', error);
      setSalesHistoryData([]);
    } finally {
      setLoadingSalesHistory(false);
    }
  };

  // Görüntülenen ayın prim ödeme durumunu çek
  const fetchCurrentBonusStatus = async (employeeId, month, year) => {
    setCurrentBonusStatus(prev => ({ ...prev, loading: true }));
    try {
      const resp = await fetch(`${API_URL}/employees/${employeeId}/bonus-payments?year=${year}`);
      const list = await resp.json();
      const found = (Array.isArray(list) ? list : []).find(s => s.period_month === month);
      const notes = found?.notes || '';
      setCurrentBonusStatus({ paid: !!found?.paid, paid_at: found?.paid_at || null, notes, loading: false });
      setBonusNotesDraft(notes);
    } catch (error) {
      console.error('Prim durumu yükleme hatası:', error);
      setCurrentBonusStatus({ paid: false, paid_at: null, notes: '', loading: false });
      setBonusNotesDraft('');
    }
  };

  // Görüntülenen ayın maaş ödeme durumunu çek
  const fetchCurrentSalaryStatus = async (employeeId, month, year) => {
    setCurrentSalaryStatus(prev => ({ ...prev, loading: true }));
    try {
      const resp = await fetch(`${API_URL}/employees/${employeeId}/salary-payment-status?year=${year}`);
      const list = await resp.json();
      const found = (Array.isArray(list) ? list : []).find(s => s.period_month === month);
      const notes = found?.notes || '';
      setCurrentSalaryStatus({ paid: !!found?.paid, paid_at: found?.paid_at || null, notes, loading: false });
      setSalaryNotesDraft(notes);
    } catch (error) {
      console.error('Maaş durumu yükleme hatası:', error);
      setCurrentSalaryStatus({ paid: false, paid_at: null, notes: '', loading: false });
      setSalaryNotesDraft('');
    }
  };

  const toggleCurrentBonusPaid = async () => {
    const sal = selectedEmployeeSalary;
    if (!sal?.employee?.id || !sal?.period) return;
    const totalBonus = (Number(sal.commission_amount) || 0) + (Number(sal.formula_bonus_amount) || 0);
    const newPaid = !currentBonusStatus.paid;
    setCurrentBonusStatus(prev => ({ ...prev, loading: true }));
    try {
      await fetch(`${API_URL}/employees/${sal.employee.id}/bonus-payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          period_month: sal.period.month,
          period_year: sal.period.year,
          paid: newPaid,
          bonus_amount: totalBonus,
          notes: bonusNotesDraft
        })
      });
      setCurrentBonusStatus({ paid: newPaid, paid_at: newPaid ? new Date().toISOString() : null, notes: bonusNotesDraft, loading: false });
      // Tablodaki "Prim Ödemesi" sütununu da yenile (vadesi geçen ay ödendiyse borç düşmeli)
      if (employees.length > 0) fetchOverdueBonus();
    } catch (error) {
      console.error('Prim durumu güncelleme hatası:', error);
      setCurrentBonusStatus(prev => ({ ...prev, loading: false }));
      toast.error('Güncellenemedi');
    }
  };

  const toggleCurrentSalaryPaid = async () => {
    const sal = selectedEmployeeSalary;
    if (!sal?.employee?.id || !sal?.period) return;
    const totalBonus = (Number(sal.commission_amount) || 0) + (Number(sal.formula_bonus_amount) || 0);
    const salaryAmount = Math.max(0, (Number(sal.total_amount) || 0) - totalBonus);
    const newPaid = !currentSalaryStatus.paid;
    setCurrentSalaryStatus(prev => ({ ...prev, loading: true }));
    try {
      await fetch(`${API_URL}/employees/${sal.employee.id}/salary-payment-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          period_month: sal.period.month,
          period_year: sal.period.year,
          paid: newPaid,
          salary_amount: salaryAmount,
          notes: salaryNotesDraft
        })
      });
      setCurrentSalaryStatus({ paid: newPaid, paid_at: newPaid ? new Date().toISOString() : null, notes: salaryNotesDraft, loading: false });
    } catch (error) {
      console.error('Maaş durumu güncelleme hatası:', error);
      setCurrentSalaryStatus(prev => ({ ...prev, loading: false }));
      toast.error('Güncellenemedi');
    }
  };

  // Not alanı blur olduğunda DB'ye kaydet (ödeme durumunu değiştirmeden)
  const saveBonusNotes = async () => {
    const sal = selectedEmployeeSalary;
    if (!sal?.employee?.id || !sal?.period) return;
    if (bonusNotesDraft === currentBonusStatus.notes) return;
    const totalBonus = (Number(sal.commission_amount) || 0) + (Number(sal.formula_bonus_amount) || 0);
    try {
      await fetch(`${API_URL}/employees/${sal.employee.id}/bonus-payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          period_month: sal.period.month,
          period_year: sal.period.year,
          paid: currentBonusStatus.paid,
          bonus_amount: totalBonus,
          notes: bonusNotesDraft
        })
      });
      setCurrentBonusStatus(prev => ({ ...prev, notes: bonusNotesDraft }));
    } catch (error) {
      console.error('Prim notu kaydetme hatası:', error);
      toast.error('Not kaydedilemedi');
    }
  };

  const saveSalaryNotes = async () => {
    const sal = selectedEmployeeSalary;
    if (!sal?.employee?.id || !sal?.period) return;
    if (salaryNotesDraft === currentSalaryStatus.notes) return;
    const totalBonus = (Number(sal.commission_amount) || 0) + (Number(sal.formula_bonus_amount) || 0);
    const salaryAmount = Math.max(0, (Number(sal.total_amount) || 0) - totalBonus);
    try {
      await fetch(`${API_URL}/employees/${sal.employee.id}/salary-payment-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          period_month: sal.period.month,
          period_year: sal.period.year,
          paid: currentSalaryStatus.paid,
          salary_amount: salaryAmount,
          notes: salaryNotesDraft
        })
      });
      setCurrentSalaryStatus(prev => ({ ...prev, notes: salaryNotesDraft }));
    } catch (error) {
      console.error('Maaş notu kaydetme hatası:', error);
      toast.error('Not kaydedilemedi');
    }
  };

  // Prim formül paketlerini getir
  const fetchBonusFormulas = async () => {
    try {
      setLoadingFormulas(true);
      const response = await fetch(`${API_URL}/bonus-formula-sets`);
      const data = await response.json();
      setBonusFormulas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Formül yükleme hatası:', error);
    } finally {
      setLoadingFormulas(false);
    }
  };

  // Formül paketi kaydet
  const handleSaveFormulaSet = async (formula) => {
    try {
      if (formula.id) {
        await fetch(`${API_URL}/bonus-formula-sets/${formula.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formula)
        });
        toast.success('Formül güncellendi!');
      } else {
        await fetch(`${API_URL}/bonus-formula-sets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formula)
        });
        toast.success('Formül eklendi!');
      }
      fetchBonusFormulas();
    } catch (error) {
      toast.error('Formül kaydedilemedi!');
    }
  };

  // Formül sil
  const handleDeleteFormulaSet = async (id) => {
    try {
      await fetch(`${API_URL}/bonus-formula-sets/${id}`, { method: 'DELETE' });
      toast.success('Formül silindi!');
      fetchBonusFormulas();
    } catch (error) {
      toast.error('Formül silinemedi!');
    }
  };

  // Yeni personel ekle / güncelle
  const handleSaveEmployee = async () => {
    if (!employeeForm.name) {
      toast.warning('Personel adı zorunludur!');
      return;
    }

    // Mağaza isimlerini al
    const storeNames = employeeForm.store_ids?.map(id => stores.find(s => s.id === id)?.name).filter(Boolean).join(', ') || '-';

    try {
      const url = editingEmployee
        ? `${API_URL}/employees/${editingEmployee.id}`
        : `${API_URL}/employees`;
      const method = editingEmployee ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(employeeForm)
      });

      const data = await response.json();
      if (data.success || response.ok) {
        if (editingEmployee) {
          // Değişiklikleri tespit et
          const changes = [];

          if (originalEmployee?.name !== employeeForm.name) {
            changes.push(`Ad Soyad: "${originalEmployee?.name}" → "${employeeForm.name}"`);
          }
          if (originalEmployee?.phone !== employeeForm.phone) {
            changes.push(`Telefon: "${originalEmployee?.phone || '-'}" → "${employeeForm.phone || '-'}"`);
          }
          if (originalEmployee?.email !== employeeForm.email) {
            changes.push(`E-posta: "${originalEmployee?.email || '-'}" → "${employeeForm.email || '-'}"`);
          }
          if (originalEmployee?.position !== employeeForm.position) {
            changes.push(`Pozisyon: "${originalEmployee?.position || '-'}" → "${employeeForm.position || '-'}"`);
          }
          if (String(originalEmployee?.salary) !== String(employeeForm.salary)) {
            changes.push(`Maaş: ₺${Number(originalEmployee?.salary || 0).toLocaleString()} → ₺${Number(employeeForm.salary || 0).toLocaleString()}`);
          }
          if (String(originalEmployee?.commission_rate) !== String(employeeForm.commission_rate)) {
            changes.push(`Komisyon: %${originalEmployee?.commission_rate || 0} → %${employeeForm.commission_rate || 0}`);
          }
          if (String(originalEmployee?.fixed_bonus) !== String(employeeForm.fixed_bonus)) {
            changes.push(`Sabit Prim: ₺${Number(originalEmployee?.fixed_bonus || 0).toLocaleString()} → ₺${Number(employeeForm.fixed_bonus || 0).toLocaleString()}`);
          }
          if (String(originalEmployee?.annual_leave_days) !== String(employeeForm.annual_leave_days)) {
            changes.push(`Yıllık İzin: ${originalEmployee?.annual_leave_days || 14} gün → ${employeeForm.annual_leave_days || 14} gün`);
          }
          if (originalEmployee?.is_sales_staff !== employeeForm.is_sales_staff) {
            changes.push(`Satış Sorumlusu: ${originalEmployee?.is_sales_staff ? 'Evet' : 'Hayır'} → ${employeeForm.is_sales_staff ? 'Evet' : 'Hayır'}`);
          }
          if (originalEmployee?.hire_date !== employeeForm.hire_date) {
            changes.push(`İşe Başlama: ${originalEmployee?.hire_date || '-'} → ${employeeForm.hire_date || '-'}`);
          }
          if (originalEmployee?.notes !== employeeForm.notes) {
            changes.push(`Notlar güncellendi`);
          }

          logService.log('personnel', 'update',
            `Personel güncellendi: ${employeeForm.name} - ${changes.length} değişiklik`, {
            entity_type: 'employee',
            entity_id: editingEmployee.id,
            details: {
              personel: employeeForm.name,
              pozisyon: employeeForm.position || '-',
              degisiklik_sayisi: changes.length,
              degisiklikler: changes.length > 0 ? changes : ['Değişiklik yapılmadı'],
              onceki_degerler: {
                ad: originalEmployee?.name,
                pozisyon: originalEmployee?.position || '-',
                maas: `₺${Number(originalEmployee?.salary || 0).toLocaleString()}`,
                komisyon: `%${originalEmployee?.commission_rate || 0}`
              },
              yeni_degerler: {
                ad: employeeForm.name,
                pozisyon: employeeForm.position || '-',
                maas: `₺${Number(employeeForm.salary || 0).toLocaleString()}`,
                komisyon: `%${employeeForm.commission_rate || 0}`
              }
            },
            status: 'success'
          });
        } else {
          // Yeni personel log'u
          logService.log('personnel', 'create',
            `Yeni personel eklendi: ${employeeForm.name} - ${employeeForm.position || 'Pozisyon belirtilmemiş'}`, {
            entity_type: 'employee',
            entity_id: data.employee?.id || data.id,
            details: {
              personel: employeeForm.name,
              telefon: employeeForm.phone || '-',
              email: employeeForm.email || '-',
              tc_no: employeeForm.tc_no ? '***' + employeeForm.tc_no.slice(-4) : '-',
              pozisyon: employeeForm.position || '-',
              departman: employeeForm.department || '-',
              magazalar: storeNames,
              ise_baslama: employeeForm.hire_date || '-',
              maas: `₺${Number(employeeForm.salary || 0).toLocaleString()}`,
              komisyon_orani: `%${employeeForm.commission_rate || 0}`,
              sabit_prim: `₺${Number(employeeForm.fixed_bonus || 0).toLocaleString()}`,
              yillik_izin: `${employeeForm.annual_leave_days || 14} gün`,
              satis_sorumlusu: employeeForm.is_sales_staff ? 'Evet' : 'Hayır'
            },
            status: 'success'
          });
        }

        toast.success(editingEmployee ? 'Personel güncellendi!' : 'Personel eklendi!');
        setShowEmployeeModal(false);
        setEditingEmployee(null);
        resetEmployeeForm();
        fetchEmployees();
        // Maaş verilerini de yenile (overtime_multiplier, daily_work_hours gibi değişiklikler tabloya yansısın)
        setTimeout(() => fetchSalaryData(), 100);
      } else {
        toast.error(data.message || 'İşlem başarısız!');
        logService.log('personnel', editingEmployee ? 'update' : 'create',
          `Personel ${editingEmployee ? 'güncellenemedi' : 'eklenemedi'}: ${employeeForm.name}`, {
          entity_type: 'employee',
          details: { personel: employeeForm.name, hata: data.message },
          status: 'error'
        });
      }
    } catch (error) {
      console.error('Personel kaydetme hatası:', error);
      toast.error('İşlem başarısız!');
      logService.log('personnel', editingEmployee ? 'update' : 'create',
        `Personel hatası: ${employeeForm.name}`, {
        entity_type: 'employee',
        details: { personel: employeeForm.name, hata: error.message },
        status: 'error'
      });
    }
  };

  // Personel sil
  const handleDeleteEmployee = async (employee) => {
    showConfirmation({
      title: 'Personel Sil',
      message: `"${employee.name}" adlı personeli silmek istediğinize emin misiniz?`,
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/employees/${employee.id}`, { method: 'DELETE' });
          const data = await response.json();
          if (data.success) {
            logService.log('personnel', 'delete',
              `Personel silindi: ${employee.name} - ${employee.position || 'Pozisyon belirtilmemiş'}`, {
              entity_type: 'employee',
              entity_id: employee.id,
              details: {
                personel: employee.name,
                telefon: employee.phone || '-',
                pozisyon: employee.position || '-',
                departman: employee.department || '-',
                magaza: employee.store_name || '-',
                maas: `₺${Number(employee.salary || 0).toLocaleString()}`,
                ise_baslama: employee.hire_date ? employee.hire_date.split('T')[0] : '-',
                satis_sorumlusu: employee.is_sales_staff ? 'Evet' : 'Hayır'
              },
              status: 'success'
            });
            toast.success('Personel silindi!');
            fetchEmployees();
          } else {
            toast.error(data.message || 'Silme başarısız!');
            logService.log('personnel', 'delete', `Personel silinemedi: ${employee.name}`, {
              entity_type: 'employee',
              entity_id: employee.id,
              details: { personel: employee.name, hata: data.message },
              status: 'error'
            });
          }
        } catch (error) {
          console.error('Personel silme hatası:', error);
          toast.error('Silme başarısız!');
          logService.log('personnel', 'delete', `Personel silme hatası: ${employee.name}`, {
            entity_type: 'employee',
            entity_id: employee.id,
            details: { personel: employee.name, hata: error.message },
            status: 'error'
          });
        }
      }
    });
  };

  // Personel düzenleme modalını aç
  const openEditModal = (employee) => {
    setEditingEmployee(employee);
    // Orijinal değerleri kaydet (değişiklik tespiti için)
    let parsedSchedule = defaultWorkSchedule;
    try {
      if (employee.work_schedule) {
        parsedSchedule = typeof employee.work_schedule === 'string' ? JSON.parse(employee.work_schedule) : employee.work_schedule;
      }
    } catch (e) {}
    const formValues = {
      name: employee.name || '',
      phone: employee.phone || '',
      email: employee.email || '',
      tc_no: employee.tc_no || '',
      position: employee.position || '',
      department: employee.department || '',
      store_ids: employee.store_ids ? (typeof employee.store_ids === 'string' ? JSON.parse(employee.store_ids) : employee.store_ids) : (employee.store_id ? [employee.store_id] : []),
      hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : '',
      birth_date: employee.birth_date ? employee.birth_date.split('T')[0] : '',
      salary: employee.salary || '',
      salary_type: employee.salary_type || 'monthly',
      hourly_rate: employee.hourly_rate || '',
      overtime_multiplier: employee.overtime_multiplier || '1.5',
      daily_work_hours: employee.daily_work_hours || '',
      work_hours_value: employee.work_hours_value != null && employee.work_hours_value !== ''
        ? employee.work_hours_value
        : (employee.daily_work_hours || ''),
      work_hours_unit: employee.work_hours_unit || 'daily',
      monthly_salary_30: employee.salary || '',
      work_schedule: parsedSchedule,
      annual_leave_days: employee.annual_leave_days || 14,
      commission_rate: employee.commission_rate || '',
      bonus_formula_set_id: employee.bonus_formula_set_id || '',
      fixed_bonus: employee.fixed_bonus || '',
      is_sales_staff: employee.is_sales_staff || false,
      notes: employee.notes || '',
      store_name: employee.store_name || ''
    };
    setOriginalEmployee({ ...formValues });
    setEmployeeForm(formValues);
    setShowEmployeeModal(true);

    // Bu personelin ücretli/ücretsiz izin günlerini çek
    setEmployeeLeaveHistory({ paid: [], unpaid: [], loading: true });
    fetch(`${API_URL}/attendance/history/${employee.id}?limit=500`)
      .then(r => r.ok ? r.json() : [])
      .then(rows => {
        const list = Array.isArray(rows) ? rows : [];
        const paid = list.filter(r => r.status === 'paid_leave').map(r => ({ date: r.date, notes: r.notes }));
        const unpaid = list.filter(r => r.status === 'unpaid_leave').map(r => ({ date: r.date, notes: r.notes }));
        setEmployeeLeaveHistory({ paid, unpaid, loading: false });
      })
      .catch(() => setEmployeeLeaveHistory({ paid: [], unpaid: [], loading: false }));
  };

  // Formu sıfırla
  const resetEmployeeForm = () => {
    setOriginalEmployee(null);
    setEmployeeForm({
      name: '', phone: '', email: '', tc_no: '', position: 'Satış Sorumlusu', department: '',
      store_ids: [], hire_date: '', birth_date: '', salary: '', salary_type: 'monthly',
      hourly_rate: '', overtime_multiplier: '1.5', daily_work_hours: '', work_hours_value: '', work_hours_unit: 'daily', monthly_salary_30: '', work_schedule: defaultWorkSchedule,
      annual_leave_days: 14, commission_rate: '', fixed_bonus: '', is_sales_staff: true, notes: ''
    });
  };

  // Çalışma programından aylık saat ve maaş hesapla
  const calculateFromSchedule = (schedule, hourly) => {
    const hr = parseFloat(hourly) || 0;
    let weeklyHours = 0;
    const dayLabels = { monday: 'Pzt', tuesday: 'Sal', wednesday: 'Çar', thursday: 'Per', friday: 'Cum', saturday: 'Cmt', sunday: 'Paz' };
    Object.entries(schedule || {}).forEach(([, day]) => {
      if (day?.active && day.start && day.end) {
        const [sh, sm] = day.start.split(':').map(Number);
        const [eh, em] = day.end.split(':').map(Number);
        const h = (eh + em / 60) - (sh + sm / 60);
        if (h > 0) weeklyHours += h;
      }
    });
    const monthlyHours = weeklyHours * 4.33;
    return { weeklyHours, monthlyHours, monthlySalary: hr * monthlyHours };
  };

  // Yoklama durumunu güncelle
  const updateAttendanceStatus = async (employeeId, status) => {
    const employee = attendanceData.find(item => item.employee_id === employeeId);
    const statusNames = {
      present: 'Geldi', absent: 'Gelmedi', late: 'Geç Kaldı',
      half_day: 'Yarım Gün', paid_leave: 'Ücretli İzin', unpaid_leave: 'Ücretsiz İzin', sick_leave: 'Raporlu'
    };
    const oldStatus = employee?.status;

    try {
      await fetch(`${API_URL}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: employeeId,
          date: attendanceDate,
          status: status,
          created_by: user?.id
        })
      });

      // Local state'i güncelle
      setAttendanceData(prev =>
        prev.map(item =>
          item.employee_id === employeeId ? { ...item, status } : item
        )
      );

      logService.log('personnel', 'update',
        `Yoklama güncellendi: ${employee?.name} - ${statusNames[status]}`, {
        entity_type: 'attendance',
        entity_id: employeeId,
        details: {
          personel: employee?.name || '-',
          tarih: attendanceDate,
          onceki_durum: statusNames[oldStatus] || '-',
          yeni_durum: statusNames[status],
          kaydeden: user?.name || '-'
        },
        status: 'success'
      });

      toast.success('Yoklama güncellendi!');
    } catch (error) {
      console.error('Yoklama güncelleme hatası:', error);
      toast.error('Güncelleme başarısız!');
      logService.log('personnel', 'update', `Yoklama hatası: ${employee?.name}`, {
        entity_type: 'attendance',
        details: { personel: employee?.name, hata: error.message },
        status: 'error'
      });
    }
  };

  // Toplu yoklama kaydet
  const saveAllAttendance = async () => {
    const statusNames = {
      present: 'Geldi', absent: 'Gelmedi', late: 'Geç Kaldı',
      half_day: 'Yarım Gün', paid_leave: 'Ücretli İzin', unpaid_leave: 'Ücretsiz İzin', sick_leave: 'Raporlu'
    };

    try {
      const attendance = attendanceData.map(item => ({
        employee_id: item.employee_id,
        status: item.status || 'present',
        check_in: item.check_in,
        check_out: item.check_out,
        notes: item.notes
      }));

      await fetch(`${API_URL}/attendance/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: attendanceDate,
          attendance,
          created_by: user?.id
        })
      });

      // Özet bilgisi hazırla
      const summary = {};
      attendanceData.forEach(item => {
        const status = item.status || 'present';
        summary[status] = (summary[status] || 0) + 1;
      });

      const summaryText = Object.entries(summary)
        .map(([status, count]) => `${statusNames[status]}: ${count}`)
        .join(', ');

      logService.log('personnel', 'update',
        `Toplu yoklama kaydedildi: ${attendanceDate} - ${attendanceData.length} personel`, {
        entity_type: 'attendance',
        details: {
          tarih: attendanceDate,
          toplam_personel: attendanceData.length,
          ozet: summaryText,
          detay: summary,
          kaydeden: user?.name || '-'
        },
        status: 'success'
      });

      toast.success('Tüm yoklamalar kaydedildi!');
    } catch (error) {
      console.error('Toplu yoklama hatası:', error);
      toast.error('Kaydetme başarısız!');
      logService.log('personnel', 'update', `Toplu yoklama hatası: ${attendanceDate}`, {
        entity_type: 'attendance',
        details: { tarih: attendanceDate, hata: error.message },
        status: 'error'
      });
    }
  };

  // İzin talebi oluştur
  const handleCreateLeaveRequest = async () => {
    if (!newLeaveRequest.employee_id || !newLeaveRequest.start_date || !newLeaveRequest.end_date) {
      toast.warning('Tüm alanları doldurun!');
      return;
    }

    const employee = employees.find(e => e.id === parseInt(newLeaveRequest.employee_id));
    const leaveTypes = {
      annual: 'Yıllık İzin', sick: 'Hastalık', unpaid: 'Ücretsiz İzin',
      maternity: 'Doğum İzni', marriage: 'Evlilik İzni', bereavement: 'Vefat İzni', other: 'Diğer'
    };

    try {
      const response = await fetch(`${API_URL}/leave-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLeaveRequest)
      });

      const data = await response.json();
      if (data.success) {
        logService.log('personnel', 'create',
          `İzin talebi oluşturuldu: ${employee?.name} - ${newLeaveRequest.days} gün ${leaveTypes[newLeaveRequest.leave_type]}`, {
          entity_type: 'leave_request',
          entity_id: data.request?.id,
          details: {
            personel: employee?.name || '-',
            izin_turu: leaveTypes[newLeaveRequest.leave_type],
            baslangic: newLeaveRequest.start_date,
            bitis: newLeaveRequest.end_date,
            gun_sayisi: newLeaveRequest.days,
            sebep: newLeaveRequest.reason || '-',
            kalan_izin: `${(employee?.annual_leave_days || 14) - (employee?.used_leave_days || 0)} gün`
          },
          status: 'success'
        });

        toast.success('İzin talebi oluşturuldu!');
        setShowLeaveModal(false);
        setNewLeaveRequest({
          employee_id: '', leave_type: 'annual', start_date: '', end_date: '', days: 0, reason: ''
        });
        fetchLeaveRequests();
      } else {
        toast.error(data.message || 'İşlem başarısız!');
        logService.log('personnel', 'create', `İzin talebi oluşturulamadı: ${employee?.name}`, {
          entity_type: 'leave_request',
          details: { personel: employee?.name, hata: data.message },
          status: 'error'
        });
      }
    } catch (error) {
      console.error('İzin talebi hatası:', error);
      toast.error('İşlem başarısız!');
      logService.log('personnel', 'create', `İzin talebi hatası: ${employee?.name}`, {
        entity_type: 'leave_request',
        details: { personel: employee?.name, hata: error.message },
        status: 'error'
      });
    }
  };

  // İzin talebini onayla/reddet
  const handleLeaveAction = async (requestId, status) => {
    const request = leaveRequests.find(r => r.id === requestId);
    const leaveTypes = {
      annual: 'Yıllık İzin', sick: 'Hastalık', unpaid: 'Ücretsiz İzin',
      maternity: 'Doğum İzni', marriage: 'Evlilik İzni', bereavement: 'Vefat İzni', other: 'Diğer'
    };
    const actionText = status === 'approved' ? 'onaylandı' : 'reddedildi';

    try {
      const response = await fetch(`${API_URL}/leave-requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          approved_by: user?.id
        })
      });

      const data = await response.json();
      if (data.success || response.ok) {
        logService.log('personnel', 'update',
          `İzin talebi ${actionText}: ${request?.employee_name} - ${request?.days} gün`, {
          entity_type: 'leave_request',
          entity_id: requestId,
          details: {
            personel: request?.employee_name || '-',
            izin_turu: leaveTypes[request?.leave_type] || request?.leave_type,
            baslangic: request?.start_date ? request.start_date.split('T')[0] : '-',
            bitis: request?.end_date ? request.end_date.split('T')[0] : '-',
            gun_sayisi: request?.days,
            karar: status === 'approved' ? 'Onaylandı' : 'Reddedildi',
            onaylayan: user?.name || '-',
            sebep: request?.reason || '-'
          },
          status: 'success'
        });

        toast.success(status === 'approved' ? 'İzin onaylandı!' : 'İzin reddedildi!');
        fetchLeaveRequests();
        fetchEmployees();
      }
    } catch (error) {
      console.error('İzin güncelleme hatası:', error);
      toast.error('İşlem başarısız!');
      logService.log('personnel', 'update', `İzin işlemi hatası: ${request?.employee_name}`, {
        entity_type: 'leave_request',
        entity_id: requestId,
        details: { personel: request?.employee_name, hata: error.message },
        status: 'error'
      });
    }
  };

  // Maaş detayını görüntüle
  const viewSalaryDetail = async (employee, month, year) => {
    try {
      const m = month || selectedMonth;
      const y = year || selectedYear;
      const response = await fetch(
        `${API_URL}/employees/${employee.id}/salary?month=${m}&year=${y}`
      );
      const data = await response.json();
      setSelectedEmployeeSalary(data);
      setShowSalaryDetailModal(true);
      // Görüntülenen ay için maaş ve prim ödeme durumlarını çek
      fetchCurrentBonusStatus(employee.id, m, y);
      fetchCurrentSalaryStatus(employee.id, m, y);
    } catch (error) {
      console.error('Maaş detay hatası:', error);
    }
  };

  // İzin günlerini hesapla
  const calculateLeaveDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // İzin tarihleri değiştiğinde gün sayısını güncelle
  useEffect(() => {
    if (newLeaveRequest.start_date && newLeaveRequest.end_date) {
      const days = calculateLeaveDays(newLeaveRequest.start_date, newLeaveRequest.end_date);
      setNewLeaveRequest(prev => ({ ...prev, days }));
    }
  }, [newLeaveRequest.start_date, newLeaveRequest.end_date]);

  const getStatusBadge = (status, partialHours) => {
    const statusMap = {
      present: { label: 'Geldi', class: 'present' },
      absent: { label: 'Gelmedi', class: 'absent' },
      late: { label: 'Geç Kaldı', class: 'late' },
      half_day: { label: 'Yarım Gün', class: 'half' },
      paid_leave: { label: 'Ücretli İzin', class: 'leave' },
      unpaid_leave: { label: 'Ücretsiz İzin', class: 'absent' },
      sick_leave: { label: 'Raporlu', class: 'sick' },
      hourly_absence: { label: partialHours ? `Saatlik Devamsızlık (${Number(partialHours).toFixed(2).replace(/\.?0+$/, '')}sa)` : 'Saatlik Devamsızlık', class: 'absent' }
    };
    const statusInfo = statusMap[status] || { label: '-', class: '' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const getLeaveStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Bekliyor', class: 'pending' },
      approved: { label: 'Onaylandı', class: 'approved' },
      rejected: { label: 'Reddedildi', class: 'rejected' },
      cancelled: { label: 'İptal', class: 'cancelled' }
    };
    const statusInfo = statusMap[status] || { label: '-', class: '' };
    return <span className={`leave-status ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const getLeaveTypeName = (type) => {
    const types = {
      annual: 'Yıllık İzin',
      sick: 'Hastalık',
      unpaid: 'Ücretsiz',
      maternity: 'Doğum',
      marriage: 'Evlilik',
      bereavement: 'Vefat',
      other: 'Diğer'
    };
    return types[type] || type;
  };

  const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  // Personel satırı hesapları (devamsızlık, kalan izin, haftalık/aylık saat, prim formülü, gecikmiş prim) —
  // web tablosu ve iOS WebView kartları (PersonnelMobileHome) aynı fonksiyonu kullanır
  const getEmployeeRow = (employee, origIdx) => {
    const empAttendance = attendanceData.find(a => a.employee_id === employee.id);
    const monthlyAbsent = empAttendance?.monthly_absent || 0;
    const totalAbsent = empAttendance?.total_absent || 0;
    const salary = salaryData[origIdx] || {};
    const remainingLeave = (employee.annual_leave_days || 14) - (employee.used_leave_days || 0);
    const ws = salary.work_schedule || (typeof employee.work_schedule === 'string' ? JSON.parse(employee.work_schedule || '{}') : employee.work_schedule) || {};
    let weeklyHrs = 0;
    Object.values(ws).forEach(day => {
      if (day?.active && day.start && day.end) {
        const [sh, sm] = day.start.split(':').map(Number);
        const [eh, em] = day.end.split(':').map(Number);
        const h = (eh + em / 60) - (sh + sm / 60);
        if (h > 0) weeklyHrs += h;
      }
    });
    const monthlyHrs = salary.total_monthly_hours || (weeklyHrs * 4.33);
    const formulaName = salary.formula_bonus_details?.find(d => d.name)?.name || (employee.bonus_formula_set_id ? bonusFormulas.find(f => f.id === employee.bonus_formula_set_id)?.name : null);
    const bonusDebt = overdueBonus[origIdx] || null;
    return { empAttendance, monthlyAbsent, totalAbsent, salary, remainingLeave, weeklyHrs, monthlyHrs, formulaName, bonusDebt };
  };

  // iOS WebView: mağaza gruplarına ayrılmış personel kartları (web tablosundaki gruplama ve sıralamayla aynı)
  const renderMobileHome = () => {
    const groupsById = {};
    employees.forEach((emp, idx) => {
      const sid = emp.store_id || 0;
      if (!groupsById[sid]) groupsById[sid] = { storeId: String(sid), name: emp.store_name || 'Mağaza Atanmamış', employees: [] };
      groupsById[sid].employees.push({ employee: emp, row: getEmployeeRow(emp, idx), origIdx: idx });
    });
    const groups = Object.values(groupsById)
      .sort((a, b) => {
        if (a.storeId === '0' && b.storeId !== '0') return 1;
        if (b.storeId === '0' && a.storeId !== '0') return -1;
        return Number(a.storeId) - Number(b.storeId);
      })
      .map(g => ({ ...g, revenue: Number((salaryData[g.employees[0]?.origIdx] || {}).store_revenue || 0) }));
    return (
      <PersonnelMobileHome
        tab={activeTab} setTab={setActiveTab}
        month={selectedMonth} setMonth={setSelectedMonth}
        year={selectedYear} setYear={setSelectedYear}
        monthNames={monthNames}
        years={[2023, 2024, 2025, 2026]}
        loading={loading}
        groups={groups}
        imageHost={API_HOST}
        onOpen={openEditModal}
        onNew={() => {
          resetEmployeeForm();
          setEditingEmployee(null);
          setOriginalEmployee(null);
          setShowEmployeeModal(true);
        }}
        onOpenLog={() => setShowLogModal(true)}
        onAbsentToday={markAbsentToday}
        onAttendance={openAttendanceModal}
        onOvertime={openOvertimeModal}
        onSalesHistory={viewSalesHistory}
        onSalaryDetail={(employee) => viewSalaryDetail(employee)}
      />
    );
  };

  return (
    <div className={mobileApp ? 'pm-mobile m-sheets' : 'personnel-container'}>
      {mobileApp ? renderMobileHome() : (<>
      <header className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/')}>
            <ArrowLeft size={18} /> Ana Sayfa
          </button>
          <h1>Personel Yönetimi</h1>
        </div>
        <div className="header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-actions">
          <button className="action-header-btn log-btn" onClick={() => setShowLogModal(true)}>
            <ScrollText size={18} /> İşlem Geçmişi
          </button>
          {activeTab === 'employees' && (
            <button className="action-header-btn" onClick={() => {
              resetEmployeeForm();
              setEditingEmployee(null);
              setOriginalEmployee(null);
              setShowEmployeeModal(true);
            }}>
              <Plus size={18} /> Yeni Personel
            </button>
          )}
        </div>
      </header>

      {/* Tab Menüsü */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'employees' ? 'active' : ''}`}
          onClick={() => setActiveTab('employees')}
        >
          <Users size={18} /> Personeller
        </button>
        <button
          className={`tab-btn ${activeTab === 'attendance' ? 'active' : ''}`}
          onClick={() => setActiveTab('attendance')}
        >
          <ClipboardList size={18} /> Devam Takip
        </button>
        <button
          className={`tab-btn ${activeTab === 'salary' ? 'active' : ''}`}
          onClick={() => setActiveTab('salary')}
        >
          <BarChart3 size={18} /> Maaş
        </button>
        <button
          className={`tab-btn ${activeTab === 'bonus' ? 'active' : ''}`}
          onClick={() => setActiveTab('bonus')}
        >
          <TrendingUp size={18} /> Prim
        </button>
        <button
          className={`tab-btn ${activeTab === 'formulas' ? 'active' : ''}`}
          onClick={() => setActiveTab('formulas')}
        >
          <Calculator size={18} /> Formüller
        </button>
        {isExchangeRoute && (
          <button
            className={`tab-btn ${activeTab === 'exchanges' ? 'active' : ''}`}
            onClick={() => setActiveTab('exchanges')}
          >
            <Repeat size={18} /> Değişim
          </button>
        )}
      </div>
      </>)}

      <div className="personnel-content">
        {/* PERSONELLER / DEVAM TAKİP / MAAŞ / PRİM TAB'I - Birleşik Tablo */}
        {!mobileApp && (activeTab === 'employees' || activeTab === 'attendance' || activeTab === 'salary' || activeTab === 'bonus') && (() => {
          // Sekmeye göre etkin kolon seti — 'employees' tam özelleştirilebilir, diğerleri sabit preset
          const effectiveColumns = activeTab === 'employees' ? visibleColumns : tabColumnPresets[activeTab];
          return (
          <div className="employees-section">
            {/* Dönem Seçici ve Sütun Ayarları */}
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#5a6c7d' }}>Dönem:</span>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
                >
                  {monthNames.map((name, index) => (
                    <option key={index} value={index + 1}>{name}</option>
                  ))}
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
                >
                  {[2023, 2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              {activeTab === 'employees' && (
                <div className="column-settings-wrapper">
                  <button
                    className="column-settings-btn"
                    onClick={() => setShowColumnSettings(!showColumnSettings)}
                  >
                    <Settings size={16} /> Sütunlar
                  </button>
                  {showColumnSettings && (
                    <div className="column-settings-dropdown">
                      <div className="column-settings-header">
                        <span>Görünür Sütunlar</span>
                        <button className="reset-columns-btn" onClick={resetColumns}>Sıfırla</button>
                      </div>
                      <div className="column-settings-list">
                        {allEmployeeColumns.map(col => (
                          <label key={col.key} className="column-toggle">
                            <input
                              type="checkbox"
                              checked={visibleColumns[col.key]}
                              onChange={() => toggleColumn(col.key)}
                            />
                            <span>{col.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {loading ? (
              <div className="loading">Yükleniyor...</div>
            ) : employees.length === 0 ? (
              <div className="empty-state">
                <Briefcase size={48} style={{ color: '#9ca3af' }} />
                <p>Henüz personel kaydı yok</p>
                <button onClick={() => setShowEmployeeModal(true)}>İlk Personeli Ekle</button>
              </div>
            ) : (
              <div className="employees-table-wrapper">
                <table className="employees-table">
                  <thead>
                    <tr>
                      {effectiveColumns.personnel && <th>Personel</th>}
                      {effectiveColumns.monthlyAbsent && <th>Devamsızlık (Ay)</th>}
                      {effectiveColumns.totalAbsent && <th>Devamsızlık (Top.)</th>}
                      {effectiveColumns.remainingLeave && <th>Kalan İzin</th>}
                      {effectiveColumns.weeklyHours && <th>Haftalık Saat</th>}
                      {effectiveColumns.monthlyHours && <th>Aylık Saat</th>}
                      {effectiveColumns.salary && <th>Maaş</th>}
                      {effectiveColumns.deduction && <th>Kesinti</th>}
                      {effectiveColumns.overtime && <th>Ek Mesai</th>}
                      {effectiveColumns.sales && <th>Satış</th>}
                      {effectiveColumns.grossSales && <th>Toplam Satış</th>}
                      {effectiveColumns.refundsTotal && <th>Toplam İade</th>}
                      {effectiveColumns.bonusEligible && <th>Net Satış</th>}
                      {effectiveColumns.bonusFormula && <th>Prim Formülü</th>}
                      {effectiveColumns.bonus && <th>Prim</th>}
                      {effectiveColumns.bonusDebt && <th>Prim Ödemesi</th>}
                      {effectiveColumns.total && <th>Toplam</th>}
                      {effectiveColumns.actions && <th style={{ textAlign: 'right' }}>İşlem</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const storeColorPalette = [
                        { header: '#dbeafe', border: '#93c5fd', row: '#eff6ff' },
                        { header: '#dcfce7', border: '#86efac', row: '#f0fdf4' },
                        { header: '#fef3c7', border: '#fcd34d', row: '#fffbeb' },
                        { header: '#fce7f3', border: '#f9a8d4', row: '#fdf2f8' },
                        { header: '#ede9fe', border: '#c4b5fd', row: '#f5f3ff' },
                        { header: '#cffafe', border: '#67e8f9', row: '#ecfeff' },
                        { header: '#ffe4e6', border: '#fda4af', row: '#fff1f2' },
                      ];
                      const noStoreColor = { header: '#e2e8f0', border: '#94a3b8', row: '#f8fafc' };
                      const storeGroups = {};
                      employees.forEach((emp, idx) => {
                        const sid = emp.store_id || 0;
                        if (!storeGroups[sid]) storeGroups[sid] = { name: emp.store_name || 'Mağaza Atanmamış', employees: [] };
                        storeGroups[sid].employees.push({ ...emp, _origIdx: idx });
                      });
                      const visibleCount = Object.values(effectiveColumns).filter(Boolean).length;
                      const rows = [];
                      Object.entries(storeGroups)
                        .sort(([a], [b]) => {
                          if (a === '0' && b !== '0') return 1;
                          if (b === '0' && a !== '0') return -1;
                          return Number(a) - Number(b);
                        })
                        .forEach(([storeId, group], gi) => {
                        const palette = storeId === '0' ? noStoreColor : storeColorPalette[gi % storeColorPalette.length];
                        const bgColor = palette.row;
                        const storeRevenue = Number((salaryData[group.employees[0]?._origIdx] || {}).store_revenue || 0);
                        rows.push(
                          <tr key={`store-${storeId}`}>
                            <td colSpan={visibleCount} style={{ padding: '10px 14px', background: palette.header, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', borderLeft: `4px solid ${palette.border}`, borderBottom: `2px solid ${palette.border}` }}>
                              <span>
                                <Store size={15} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                                {group.name} ({group.employees.length} kişi)
                              </span>
                            </td>
                          </tr>
                        );
                        group.employees.forEach(employee => {
                          const { empAttendance, monthlyAbsent, totalAbsent, salary, remainingLeave, weeklyHrs, monthlyHrs, formulaName } = getEmployeeRow(employee, employee._origIdx);
                          rows.push(
                            <tr key={employee.id} onClick={() => openEditModal(employee)} style={{ cursor: 'pointer', background: bgColor }}>
                              {effectiveColumns.personnel && (
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  {employee.photo_url ? (
                                    <img src={`${API_HOST}${employee.photo_url}`} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover' }} />
                                  ) : (
                                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg, #00bfa6, #0d9488)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.85rem', flexShrink: 0 }}>
                                      {employee.name?.charAt(0) || '?'}
                                    </div>
                                  )}
                                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{employee.name}</span>
                                </div>
                              </td>
                              )}
                              {effectiveColumns.monthlyAbsent && (
                              <td>
                                <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, background: monthlyAbsent > 0 ? '#fee2e2' : 'rgba(255,255,255,0.7)', color: monthlyAbsent > 0 ? '#dc2626' : '#16a34a' }}>
                                  {monthlyAbsent} gün
                                </span>
                              </td>
                              )}
                              {effectiveColumns.totalAbsent && (
                              <td>
                                <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, background: totalAbsent > 5 ? '#fef3c7' : 'rgba(255,255,255,0.7)', color: totalAbsent > 5 ? '#d97706' : '#64748b' }}>
                                  {totalAbsent} gün
                                </span>
                              </td>
                              )}
                              {effectiveColumns.remainingLeave && (
                              <td>
                                <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, background: remainingLeave <= 3 ? '#fee2e2' : remainingLeave <= 7 ? '#fef3c7' : '#f0fdf4', color: remainingLeave <= 3 ? '#dc2626' : remainingLeave <= 7 ? '#d97706' : '#16a34a' }}>
                                  {remainingLeave} gün
                                </span>
                              </td>
                              )}
                              {effectiveColumns.weeklyHours && (
                              <td>
                                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
                                  {weeklyHrs > 0 ? `${weeklyHrs.toFixed(1)} sa` : '-'}
                                </span>
                              </td>
                              )}
                              {effectiveColumns.monthlyHours && (
                              <td>
                                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
                                  {monthlyHrs > 0 ? `${Math.round(monthlyHrs)} sa` : '-'}
                                </span>
                              </td>
                              )}
                              {effectiveColumns.salary && (
                              <td>₺{Number(salary.base_salary || employee.salary || 0).toLocaleString()}</td>
                              )}
                              {effectiveColumns.deduction && (
                              <td style={{ color: Number(salary.deductions || 0) > 0 ? '#dc2626' : '#94a3b8' }}>
                                {Number(salary.deductions || 0) > 0 ? `-₺${Number(salary.deductions).toLocaleString()}` : '-'}
                              </td>
                              )}
                              {effectiveColumns.overtime && (
                              <td title={Number(salary.overtime_hours || 0) > 0 ? `${salary.overtime_hours} saat × ${salary.overtime_multiplier || 1.5}x` : ''} style={{ color: Number(salary.overtime_amount || 0) > 0 ? '#16a34a' : '#94a3b8' }}>
                                {Number(salary.overtime_amount || 0) > 0
                                  ? <span>+₺{Number(salary.overtime_amount).toLocaleString()} <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>({salary.overtime_hours} sa)</span></span>
                                  : '-'}
                              </td>
                              )}
                              {effectiveColumns.sales && (
                              <td>₺{Number(salary.sales_total || 0).toLocaleString()}</td>
                              )}
                              {effectiveColumns.grossSales && (
                              <td style={{ fontWeight: 600, color: '#0f766e' }}>
                                ₺{Number(salary.gross_sales_total || 0).toLocaleString()}
                              </td>
                              )}
                              {effectiveColumns.refundsTotal && (
                              <td style={{ color: Number(salary.refunds_total || 0) > 0 ? '#dc2626' : '#94a3b8' }}>
                                {Number(salary.refunds_total || 0) > 0 ? `-₺${Number(salary.refunds_total).toLocaleString()}` : '-'}
                              </td>
                              )}
                              {effectiveColumns.bonusEligible && (
                              <td style={{ fontWeight: 700, color: '#7c3aed', background: 'rgba(124,58,237,0.06)' }}
                                  title="Prim hesabında kullanılan tutar: satışlar − iadeler + değişim farkı, paylaşımlı satışlar bölünmüş">
                                ₺{Number(salary.bonus_eligible_sales_total ?? salary.sales_total ?? 0).toLocaleString()}
                              </td>
                              )}
                              {effectiveColumns.bonusFormula && (
                              <td>
                                <span style={{ fontSize: '0.78rem', color: formulaName ? '#7c3aed' : '#94a3b8', fontWeight: formulaName ? 600 : 400 }}>
                                  {formulaName || '-'}
                                </span>
                              </td>
                              )}
                              {effectiveColumns.bonus && (
                              <td style={{ color: Number(salary.formula_bonus_amount || 0) > 0 ? '#16a34a' : '#94a3b8' }}>
                                {Number(salary.formula_bonus_amount || 0) > 0 ? `+₺${Number(salary.formula_bonus_amount).toLocaleString()}` : '-'}
                              </td>
                              )}
                              {effectiveColumns.bonusDebt && (() => {
                                const debt = overdueBonus[employee._origIdx];
                                if (!debt) {
                                  return <td style={{ color: '#94a3b8' }}>-</td>;
                                }
                                return (
                                  <td title={`${monthNames[debt.period_month - 1]} ${debt.period_year} primi vadesi geçmiş — ödenmedi olarak işaretli`}
                                      style={{ background: '#fef2f2' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <AlertTriangle size={14} style={{ color: '#dc2626', flexShrink: 0 }} />
                                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ color: '#dc2626', fontWeight: 700, fontSize: '0.85rem' }}>
                                          ₺{debt.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                        </span>
                                        <span style={{ color: '#991b1b', fontSize: '0.7rem', fontWeight: 600 }}>
                                          {monthNames[debt.period_month - 1]} borcu
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                );
                              })()}
                              {effectiveColumns.total && (
                              <td style={{ fontWeight: 700, color: '#0d9488' }}>
                                ₺{Number(salary.total_amount || 0).toLocaleString()}
                              </td>
                              )}
                              {effectiveColumns.actions && (() => {
                                // Sekmeye göre hangi aksiyon butonlarının gösterileceğini belirle
                                const showAttendanceActions = activeTab === 'employees' || activeTab === 'attendance';
                                const showSalesHistory = activeTab === 'employees' || activeTab === 'bonus';
                                const showSalaryDetail = activeTab === 'employees' || activeTab === 'salary' || activeTab === 'bonus';
                                return (
                                <td>
                                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                                    <button className="btn-icon" onClick={(e) => { e.stopPropagation(); openEditModal(employee); }} title="Düzenle"><Pencil size={14} /></button>
                                    {showAttendanceActions && (
                                      <>
                                        <button className="btn-icon" onClick={(e) => { e.stopPropagation(); markAbsentToday(employee); }} title={empAttendance?.status ? 'Bugün zaten işaretlendi' : 'Bugün gelmedi'} disabled={!!empAttendance?.status} style={{ color: empAttendance?.status ? '#d1d5db' : '#ef4444', cursor: empAttendance?.status ? 'not-allowed' : 'pointer' }}><ClipboardList size={14} /></button>
                                        <button className="btn-icon" onClick={(e) => { e.stopPropagation(); openAttendanceModal(employee); }} title="Yoklama ekle" style={{ color: '#f59e0b' }}><Plus size={14} /></button>
                                        <button className="btn-icon" onClick={(e) => { e.stopPropagation(); openOvertimeModal(employee); }} title="Mesai ekle" style={{ color: '#3b82f6' }}><Clock size={14} /></button>
                                      </>
                                    )}
                                    {showSalesHistory && (
                                      <button className="btn-icon" onClick={(e) => { e.stopPropagation(); viewSalesHistory(employee); }} title="Satış geçmişi" style={{ color: '#6366f1' }}><ShoppingCart size={14} /></button>
                                    )}
                                    {showSalaryDetail && (
                                      <button className="btn-icon" onClick={(e) => { e.stopPropagation(); viewSalaryDetail(employee); }} title="Maaş detay" style={{ color: '#8b5cf6' }}><BarChart3 size={14} /></button>
                                    )}
                                    <button className="btn-icon delete" onClick={(e) => { e.stopPropagation(); handleDeleteEmployee(employee); }} title="Sil"><Trash2 size={14} /></button>
                                  </div>
                                </td>
                                );
                              })()}
                            </tr>
                          );
                        });
                        rows.push(
                          <tr key={`store-total-${storeId}`}>
                            <td colSpan={visibleCount} style={{ padding: '8px 14px', background: palette.header, textAlign: 'right', borderTop: `2px solid ${palette.border}`, borderBottom: `2px solid ${palette.border}` }}>
                              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b' }}>Aylık Ciro (Tamirat Dahil): </span>
                              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>₺{storeRevenue.toLocaleString()}</span>
                            </td>
                          </tr>
                        );
                      });
                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          );
        })()}

        {/* Yoklama ve Maaş sekmeleri birleşik tabloya taşındı */}

        {/* SATIŞ SORUMLULARI TAB'I */}
        {activeTab === 'salesStaff' && (
          <div className="sales-staff-section">
            {loadingSalesData ? (
              <div className="loading">Yükleniyor...</div>
            ) : (
              <>
                {/* Satış Sorumluları Listesi */}
                <div className="sales-staff-grid">
                  <h3>Satış Sorumluları</h3>
                  {salesStaffList.length === 0 ? (
                    <div className="empty-state">
                      <User size={48} style={{ color: '#9ca3af' }} />
                      <p>Henüz satış sorumlusu yok</p>
                    </div>
                  ) : (
                    <div className="staff-cards">
                      {salesStaffList.map(employee => {
                        // Bu personelin satışlarını hesapla (isim eşleştirmesi)
                        const employeeSales = salesHistory.filter(s =>
                          s.staff_name &&
                          (s.staff_name.toLowerCase().includes(employee.name.toLowerCase()) ||
                           employee.name.toLowerCase().includes(s.staff_name.toLowerCase()))
                        );
                        const totalSales = employeeSales.reduce((sum, s) => sum + getSalePaidAmount(s), 0);
                        const salesCount = employeeSales.length;

                        return (
                          <div key={employee.id} className="sales-staff-card">
                            <div className="staff-card-header">
                              <div className="staff-avatar-large">
                                {employee.name.charAt(0)}
                              </div>
                              <div className="staff-info">
                                <h4>{employee.name} <span style={{ color: '#6b7280', fontSize: '0.8em', fontWeight: 'normal' }}>(ID: {employee.id})</span></h4>
                                {employee.position && <span className="staff-position">{employee.position}</span>}
                                {employee.phone && <span className="staff-phone">{employee.phone}</span>}
                              </div>
                            </div>
                            <div className="staff-stats">
                              <div className="stat-item">
                                <span className="stat-label">Satış</span>
                                <span className="stat-value">{salesCount}</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-label">Toplam</span>
                                <span className="stat-value">₺{totalSales.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                              </div>
                              {(() => {
                                const sal = salesStaffSalaryMap[employee.id];
                                if (!sal) return null;
                                const commission = Number(sal.commission_amount) || 0;
                                const formulaBonus = Number(sal.formula_bonus_amount) || 0;
                                const totalBonus = commission + formulaBonus;
                                if (totalBonus <= 0) return null;
                                return (
                                  <div className="stat-item">
                                    <span className="stat-label">Prim</span>
                                    <span className="stat-value" style={{ color: '#d97706' }}>₺{totalBonus.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Geçmiş Satışlar Listesi */}
                <div className="sales-history-section">
                  <h3>Geçmiş Satışlar</h3>
                  {salesHistory.length === 0 ? (
                    <div className="empty-state">
                      <ClipboardList size={48} style={{ color: '#9ca3af' }} />
                      <p>Henüz satış kaydı yok</p>
                    </div>
                  ) : (
                    <div className="sales-history-table">
                      <div className="table-header">
                        <span className="col-id">Fiş No</span>
                        <span className="col-date">Tarih</span>
                        <span className="col-staff">Satış Sorumlusu</span>
                        <span className="col-customer">Müşteri</span>
                        <span className="col-total">Tutar</span>
                        <span className="col-payment">Ödeme</span>
                      </div>
                      <div className="table-body">
                        {salesHistory.map(sale => (
                          <div key={sale.id} className="table-row">
                            <span className="col-id">{sale.receipt_number || sale.id}</span>
                            <span className="col-date">
                              {new Date(sale.created_at).toLocaleDateString('tr-TR')}
                              <small>{new Date(sale.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</small>
                            </span>
                            <span className="col-staff">
                              {sale.staff_name || '-'}
                            </span>
                            <span className="col-customer">
                              {sale.customer_name || 'Genel Müşteri'}
                            </span>
                            <span className="col-total total-amount">
                              ₺{getSalePaidAmount(sale).toLocaleString()}
                            </span>
                            <span className="col-payment">
                              <span className={`payment-badge ${sale.payment_method?.toLowerCase().replace(' ', '-')}`}>
                                {sale.payment_method}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* MASRAFLAR TAB'I */}
        {/* FORMÜLLER TAB'I */}
        {activeTab === 'formulas' && (
          <div className="formulas-section">
            {loadingFormulas ? (
              <div className="loading">Yükleniyor...</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '10px' }}>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', flex: 1 }}>
                    Her formül paketi mağaza ciro ve kişisel satış kademeleri içerir. Personele hangi formülün uygulanacağını personel düzenleme ekranından seçin.
                  </p>
                  <button className="action-header-btn"
                    onClick={() => handleSaveFormulaSet({ name: 'Yeni Formül', store_tiers: [{ threshold: 0, bonus_percent: 0 }], employee_tiers: [{ threshold: 0, bonus_percent: 0 }] })}
                    style={{ padding: '6px 14px', fontSize: '0.82rem', flexShrink: 0 }}>
                    <Plus size={15} /> Yeni Formül Paketi
                  </button>
                  <button
                    onClick={async () => {
                      if (dirtyFormulaIds.size === 0) {
                        toast.info('Kaydedilecek değişiklik yok');
                        return;
                      }
                      setSavingFormulas(true);
                      try {
                        const dirty = bonusFormulas.filter(f => dirtyFormulaIds.has(f.id));
                        for (const f of dirty) {
                          await fetch(`${API_URL}/bonus-formula-sets/${f.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              ...f,
                              store_tiers: (f.store_tiers || []).map(t => ({
                                threshold: Number(t.threshold) || 0,
                                bonus_percent: parseFloat(String(t.bonus_percent).replace(',', '.')) || 0
                              })),
                              employee_tiers: (f.employee_tiers || []).map(t => ({
                                threshold: Number(t.threshold) || 0,
                                bonus_percent: parseFloat(String(t.bonus_percent).replace(',', '.')) || 0
                              }))
                            })
                          });
                        }
                        setDirtyFormulaIds(new Set());
                        toast.success(`${dirty.length} formül kaydedildi`);
                        fetchBonusFormulas();
                      } catch (err) {
                        console.error(err);
                        toast.error('Kaydedilemedi');
                      } finally {
                        setSavingFormulas(false);
                      }
                    }}
                    disabled={savingFormulas || dirtyFormulaIds.size === 0}
                    style={{
                      padding: '6px 14px',
                      fontSize: '0.82rem',
                      flexShrink: 0,
                      borderRadius: '8px',
                      border: 'none',
                      background: dirtyFormulaIds.size === 0 || savingFormulas ? '#94a3b8' : '#0d9488',
                      color: '#fff',
                      cursor: dirtyFormulaIds.size === 0 || savingFormulas ? 'not-allowed' : 'pointer',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {savingFormulas ? 'Kaydediliyor...' : `Kaydet${dirtyFormulaIds.size > 0 ? ` (${dirtyFormulaIds.size})` : ''}`}
                  </button>
                </div>

                {bonusFormulas.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #e2e8f0' }}>
                    <Calculator size={40} style={{ marginBottom: '8px' }} /><p>Henüz formül paketi yok</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {bonusFormulas.map(fSet => {
                      const storeTiers = fSet.store_tiers || [];
                      const empTiers = fSet.employee_tiers || [];
                      return (
                        <div key={fSet.id} style={{ padding: '20px', background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                          {/* Başlık */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                            <input type="text" value={fSet.name || ''}
                              onChange={e => {
                                setBonusFormulas(prev => prev.map(f => f.id === fSet.id ? { ...f, name: e.target.value } : f));
                                markFormulaDirty(fSet.id);
                              }}
                              style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', border: 'none', borderBottom: '2px solid transparent', padding: '2px 4px', background: 'transparent', flex: 1 }}
                              onFocus={e => e.target.style.borderBottomColor = '#0d9488'}
                              onBlurCapture={e => e.target.style.borderBottomColor = 'transparent'} />
                            {dirtyFormulaIds.has(fSet.id) && (
                              <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#fef3c7', color: '#92400e', fontSize: '0.7rem', fontWeight: 700 }}>Kaydedilmedi</span>
                            )}
                            <button onClick={() => handleDeleteFormulaSet(fSet.id)}
                              style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Trash2 size={14} /> Sil
                            </button>
                          </div>

                          {/* Mağaza Kademeleri */}
                          <div style={{ marginBottom: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                              <Store size={15} style={{ color: '#0d9488' }} />
                              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0d9488' }}>Mağaza Ciro Kademeleri</span>
                              <button onClick={() => {
                                const t = [...storeTiers, { threshold: 0, bonus_percent: 0 }];
                                setBonusFormulas(prev => prev.map(f => f.id === fSet.id ? { ...f, store_tiers: t } : f));
                                markFormulaDirty(fSet.id);
                              }}
                                style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: '5px', border: '1px solid #99f6e4', background: '#f0fdfa', color: '#0d9488', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}>+ Kademe</button>
                            </div>
                            {storeTiers.map((tier, idx) => (
                              <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', padding: '8px 12px', background: '#f0fdfa', borderRadius: '8px', flexWrap: 'nowrap' }}>
                                <span style={{ fontSize: '0.82rem', color: '#475569', whiteSpace: 'nowrap', flexShrink: 0 }}>Ciro ≥</span>
                                <div style={{ width: '130px', flexShrink: 0 }}><PriceInput value={Number(tier.threshold) || 0} hideText
                                  style={{ width: '100%', padding: '5px 8px', border: '1.5px solid #99f6e4', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 700, color: '#0d9488', boxSizing: 'border-box' }}
                                  onChange={val => {
                                    const t = [...storeTiers]; t[idx] = { ...t[idx], threshold: val };
                                    setBonusFormulas(prev => prev.map(f => f.id === fSet.id ? { ...f, store_tiers: t } : f));
                                    markFormulaDirty(fSet.id);
                                  }} /></div>
                                <span style={{ fontSize: '0.82rem', color: '#475569', whiteSpace: 'nowrap' }}>→ maaşın %</span>
                                <input type="text" inputMode="decimal" value={tier.bonus_percent || ''}
                                  onChange={e => {
                                    const v = e.target.value.replace(/[^0-9.,]/g, '');
                                    const t = [...storeTiers]; t[idx] = { ...t[idx], bonus_percent: v };
                                    setBonusFormulas(prev => prev.map(f => f.id === fSet.id ? { ...f, store_tiers: t } : f));
                                    markFormulaDirty(fSet.id);
                                  }}
                                  style={{ width: '65px', padding: '5px 8px', border: '1.5px solid #99f6e4', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 700, color: '#0d9488', textAlign: 'center' }} />
                                <span style={{ fontSize: '0.82rem', color: '#475569' }}>prim</span>
                                {storeTiers.length > 1 && (
                                  <button onClick={() => {
                                    const t = storeTiers.filter((_, i) => i !== idx);
                                    setBonusFormulas(prev => prev.map(f => f.id === fSet.id ? { ...f, store_tiers: t } : f));
                                    markFormulaDirty(fSet.id);
                                  }}
                                    style={{ marginLeft: 'auto', padding: '3px', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer' }}><Trash2 size={13} /></button>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Kişisel Kademeleri */}
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                              <User size={15} style={{ color: '#2563eb' }} />
                              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2563eb' }}>Kişisel Satış Kademeleri</span>
                              <button onClick={() => {
                                const t = [...empTiers, { threshold: 0, bonus_percent: 0 }];
                                setBonusFormulas(prev => prev.map(f => f.id === fSet.id ? { ...f, employee_tiers: t } : f));
                                markFormulaDirty(fSet.id);
                              }}
                                style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: '5px', border: '1px solid #93c5fd', background: '#eff6ff', color: '#2563eb', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}>+ Kademe</button>
                            </div>
                            {empTiers.map((tier, idx) => {
                              const threshold = Number(tier.threshold) || 0;
                              const prevThreshold = idx > 0 ? (Number(empTiers[idx - 1].threshold) || 0) : 0;
                              const percent = parseFloat(String(tier.bonus_percent).replace(',', '.')) || 0;
                              const base = Math.max(0, threshold - prevThreshold);
                              const marginal = base * percent / 100;
                              let bonusAmount = marginal;
                              for (let i = 0; i < idx; i++) {
                                const t = Number(empTiers[i].threshold) || 0;
                                const pt = i > 0 ? (Number(empTiers[i - 1].threshold) || 0) : 0;
                                const p = parseFloat(String(empTiers[i].bonus_percent).replace(',', '.')) || 0;
                                bonusAmount += Math.max(0, t - pt) * p / 100;
                              }
                              return (
                              <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', padding: '8px 12px', background: '#eff6ff', borderRadius: '8px', flexWrap: 'nowrap' }}>
                                <span style={{ fontSize: '0.82rem', color: '#475569', whiteSpace: 'nowrap', flexShrink: 0 }}>Satış ≥</span>
                                <div style={{ width: '130px', flexShrink: 0 }}><PriceInput value={Number(tier.threshold) || 0} hideText
                                  style={{ width: '100%', padding: '5px 8px', border: '1.5px solid #93c5fd', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 700, color: '#2563eb', boxSizing: 'border-box' }}
                                  onChange={val => {
                                    const t = [...empTiers]; t[idx] = { ...t[idx], threshold: val };
                                    setBonusFormulas(prev => prev.map(f => f.id === fSet.id ? { ...f, employee_tiers: t } : f));
                                    markFormulaDirty(fSet.id);
                                  }} /></div>
                                <span style={{ fontSize: '0.82rem', color: '#475569', whiteSpace: 'nowrap' }}>→ satışın %</span>
                                <input type="text" inputMode="decimal" value={tier.bonus_percent || ''}
                                  onChange={e => {
                                    const v = e.target.value.replace(/[^0-9.,]/g, '');
                                    const t = [...empTiers]; t[idx] = { ...t[idx], bonus_percent: v };
                                    setBonusFormulas(prev => prev.map(f => f.id === fSet.id ? { ...f, employee_tiers: t } : f));
                                    markFormulaDirty(fSet.id);
                                  }}
                                  style={{ width: '65px', padding: '5px 8px', border: '1.5px solid #93c5fd', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 700, color: '#2563eb', textAlign: 'center' }} />
                                <span style={{ fontSize: '0.82rem', color: '#475569' }}>prim</span>
                                <span style={{ marginLeft: 'auto', padding: '4px 10px', background: '#fff', border: '1px solid #93c5fd', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap' }}>
                                  = ₺{bonusAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                {empTiers.length > 1 && (
                                  <button onClick={() => {
                                    const t = empTiers.filter((_, i) => i !== idx);
                                    setBonusFormulas(prev => prev.map(f => f.id === fSet.id ? { ...f, employee_tiers: t } : f));
                                    markFormulaDirty(fSet.id);
                                  }}
                                    style={{ padding: '3px', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer' }}><Trash2 size={13} /></button>
                                )}
                              </div>
                              );
                            })}
                          </div>

                          <div style={{ marginTop: '12px', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px', fontSize: '0.75rem', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <AlertTriangle size={14} /> Prim ancak hem mağaza ciro hedefi hem de kişisel satış hedefi aşıldığında ödenir. İkisi de aşılmazsa ne mağaza ne kişisel prim ödenmez; ikisi de aşılırsa ikisi de ödenir. En yüksek aşılan kademe uygulanır.
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* DEĞİŞİM TAB'I */}
        {activeTab === 'exchanges' && (
          <div className="exchanges-section">
            {/* Filtreler */}
            <div className="expenses-filters">
              <div className="filter-group">
                <label><Calendar size={16} /> Ay</label>
                <select
                  value={exchangeMonth}
                  onChange={(e) => setExchangeMonth(Number(e.target.value))}
                >
                  {monthNames.map((name, index) => (
                    <option key={index} value={index + 1}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label><Calendar size={16} /> Yıl</label>
                <select
                  value={exchangeYear}
                  onChange={(e) => setExchangeYear(Number(e.target.value))}
                >
                  {[2023, 2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label><Store size={16} /> Mağaza</label>
                <select
                  value={exchangeStoreFilter}
                  onChange={(e) => setExchangeStoreFilter(e.target.value)}
                >
                  <option value="all">Tüm Mağazalar</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Özet kart */}
            <div className="expenses-summary" style={{ marginBottom: '16px' }}>
              <div className="expense-store-card">
                <h4>Toplam Değişim</h4>
                <div className="expense-total">{exchangeReceipts.length}</div>
                <div className="expense-count">fiş</div>
              </div>
              <div className="expense-store-card">
                <h4>İade Toplamı</h4>
                <div className="expense-total">
                  ₺{exchangeReceipts.reduce((s, r) => s + Number(r.return_total || 0), 0)
                    .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="expense-store-card">
                <h4>Yeni Satış Toplamı</h4>
                <div className="expense-total">
                  ₺{exchangeReceipts.reduce((s, r) => s + Number(r.new_sale_total || 0), 0)
                    .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="expense-store-card total">
                <h4>Net Fark</h4>
                <div className="expense-total">
                  ₺{exchangeReceipts.reduce((s, r) => s + Number(r.computed_difference || 0), 0)
                    .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            {/* Liste */}
            {loadingExchanges ? (
              <div className="loading">Yükleniyor...</div>
            ) : exchangeReceipts.length === 0 ? (
              <div className="empty-state">
                <Repeat size={48} style={{ color: '#9ca3af' }} />
                <p>Bu tarih aralığında değişim fişi bulunamadı</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {exchangeReceipts.map(r => {
                  const diff = Number(r.computed_difference || 0);
                  const diffColor = diff > 0 ? '#b45309' : diff < 0 ? '#065f46' : '#475569';
                  const diffBg = diff > 0 ? '#fef3c7' : diff < 0 ? '#d1fae5' : '#f1f5f9';
                  const diffLabel = diff > 0 ? 'Müşteri Ek Ödedi' : diff < 0 ? 'Müşteriye Para Üstü' : 'Fark Yok';
                  return (
                    <div key={r.exchange_id} style={{
                      padding: '16px',
                      background: '#fff',
                      borderRadius: '14px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                    }}>
                      {/* Üst başlık */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px dashed #e2e8f0' }}>
                        <div>
                          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Repeat size={16} style={{ color: '#0d9488' }} />
                            Değişim Fişi #{r.exchange_id}
                            {r.store_name && (
                              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', background: '#f1f5f9', padding: '2px 8px', borderRadius: '6px' }}>
                                {r.store_name}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '4px' }}>
                            {r.customer_name || r.exchange_customer_name || 'Müşteri bilgisi yok'} •
                            {' '}{new Date(r.exchange_created_at).toLocaleDateString('tr-TR')} {new Date(r.exchange_created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        <div style={{
                          padding: '6px 14px',
                          borderRadius: '10px',
                          background: diffBg,
                          color: diffColor,
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          textAlign: 'right',
                          minWidth: '160px'
                        }}>
                          <div style={{ fontSize: '0.7rem', fontWeight: 600, opacity: 0.8 }}>{diffLabel}</div>
                          <div>₺{Math.abs(diff).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
                        </div>
                      </div>

                      {/* İki kolon: İade edilen / Alınan */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        {/* Sol: İade edilen (orijinal fişten) */}
                        <div style={{ padding: '12px', background: '#fef2f2', borderRadius: '10px', border: '1px solid #fecaca' }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#991b1b', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>İade Edilenler (Orijinal Fiş #{r.original_sale_id})</span>
                            <span>₺{Number(r.return_total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#7f1d1d', marginBottom: '8px' }}>
                            <User size={11} style={{ verticalAlign: 'middle' }} /> İlk Kasiyer: <strong>{r.original_staff_name || '-'}</strong>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {(r.return_items || []).length === 0 ? (
                              <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>Ürün yok</div>
                            ) : (
                              r.return_items.map((it, i) => (
                                <div key={i} style={{ fontSize: '0.78rem', display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '4px 6px', background: '#fff', borderRadius: '6px' }}>
                                  <span style={{ color: '#0f172a' }}>{it.product_name} {it.quantity > 1 && <small style={{ color: '#64748b' }}>×{it.quantity}</small>}</span>
                                  <span style={{ color: '#991b1b', fontWeight: 600 }}>₺{Number(it.total_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Sağ: Alınan (yeni satış) */}
                        <div style={{ padding: '12px', background: '#ecfdf5', borderRadius: '10px', border: '1px solid #a7f3d0' }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#065f46', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Alınanlar (Yeni Fiş #{r.new_sale_id})</span>
                            <span>₺{Number(r.new_sale_total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#064e3b', marginBottom: '8px' }}>
                            <User size={11} style={{ verticalAlign: 'middle' }} /> İkinci Kasiyer: <strong>{r.new_sale_staff_name || '-'}</strong>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {(r.new_sale_items || []).length === 0 ? (
                              <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>Ürün yok</div>
                            ) : (
                              r.new_sale_items.map((it, i) => (
                                <div key={i} style={{ fontSize: '0.78rem', display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '4px 6px', background: '#fff', borderRadius: '6px' }}>
                                  <span style={{ color: '#0f172a' }}>{it.product_name} {it.quantity > 1 && <small style={{ color: '#64748b' }}>×{it.quantity}</small>}</span>
                                  <span style={{ color: '#065f46', fontWeight: 600 }}>₺{Number(it.total_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Alt bilgi */}
                      {(r.payment_method || r.notes || r.refund_staff_name) && (
                        <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0', display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '0.72rem', color: '#64748b' }}>
                          {r.refund_staff_name && r.refund_staff_name !== r.new_sale_staff_name && (
                            <span>İşlemi Yapan: <strong style={{ color: '#334155' }}>{r.refund_staff_name}</strong></span>
                          )}
                          {r.payment_method && <span>Ödeme: <strong style={{ color: '#334155' }}>{r.payment_method}</strong></span>}
                          {r.notes && <span style={{ fontStyle: 'italic' }}>{r.notes}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* PERSONEL MODAL */}
      {showEmployeeModal && (
        <div className={`modal-overlay ${closingModal === 'employee' ? 'closing' : ''}`}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEmployee ? 'Personel Düzenle' : 'Yeni Personel'}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('employee', setShowEmployeeModal)}>✕</button>
            </div>
            <div className="modal-body">
              {/* Profil Fotoğrafı */}
              {editingEmployee && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ position: 'relative' }}>
                    {editingEmployee.photo_url ? (
                      <img
                        src={`${API_HOST}${editingEmployee.photo_url}`}
                        alt={editingEmployee.name}
                        style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #00bfa6' }}
                      />
                    ) : (
                      <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #00bfa6, #0d9488)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '2rem', fontWeight: 700 }}>
                        {employeeForm.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Profil Fotoğrafı</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <label style={{ padding: '6px 14px', borderRadius: '8px', background: '#00bfa6', color: '#fff', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                        Fotoğraf Yükle
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('photo', file);
                            try {
                              const res = await fetch(`${API_URL}/employees/${editingEmployee.id}/photo`, { method: 'POST', body: formData });
                              const data = await res.json();
                              if (data.success) {
                                setEditingEmployee({...editingEmployee, photo_url: data.photo_url});
                                toast.success('Profil fotoğrafı güncellendi');
                                fetchEmployees();
                              } else {
                                toast.error(data.error || 'Yükleme başarısız');
                              }
                            } catch { toast.error('Fotoğraf yüklenemedi'); }
                            e.target.value = '';
                          }}
                        />
                      </label>
                      {editingEmployee.photo_url && (
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API_URL}/employees/${editingEmployee.id}/photo`, { method: 'DELETE' });
                              const data = await res.json();
                              if (data.success) {
                                setEditingEmployee({...editingEmployee, photo_url: null});
                                toast.success('Fotoğraf kaldırıldı');
                                fetchEmployees();
                              }
                            } catch { toast.error('Fotoğraf silinemedi'); }
                          }}
                          style={{ padding: '6px 14px', borderRadius: '8px', background: '#fee2e2', color: '#dc2626', border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                        >
                          Kaldır
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="form-grid">
                <div className="form-group">
                  <label>Ad Soyad *</label>
                  <input
                    type="text"
                    value={employeeForm.name}
                    onChange={e => setEmployeeForm({...employeeForm, name: e.target.value})}
                    placeholder="Personel adı..."
                  />
                </div>
                <div className="form-group">
                  <label>Telefon</label>
                  <input
                    type="tel"
                    value={employeeForm.phone}
                    onChange={e => setEmployeeForm({...employeeForm, phone: e.target.value})}
                    placeholder="0532 XXX XXXX"
                  />
                </div>
                <div className="form-group">
                  <label>E-posta</label>
                  <input
                    type="email"
                    value={employeeForm.email}
                    onChange={e => setEmployeeForm({...employeeForm, email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>TC Kimlik No</label>
                  <input
                    type="text"
                    value={employeeForm.tc_no}
                    onChange={e => setEmployeeForm({...employeeForm, tc_no: e.target.value})}
                    placeholder="11 haneli TC No"
                    maxLength="11"
                  />
                </div>
                <div className="form-group">
                  <label>İşe Başlama Tarihi</label>
                  <input
                    type="date"
                    value={employeeForm.hire_date}
                    onChange={e => setEmployeeForm({...employeeForm, hire_date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Doğum Tarihi</label>
                  <input
                    type="date"
                    value={employeeForm.birth_date}
                    onChange={e => setEmployeeForm({...employeeForm, birth_date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Maaş (₺)</label>
                  <PriceInput
                    value={parseFloat(employeeForm.salary) || 0}
                    onChange={(val) => setEmployeeForm({...employeeForm, salary: val})}
                  />
                </div>
                <div className="form-group">
                  <label>Yıllık İzin Hakkı (Gün)</label>
                  <input
                    type="number"
                    value={employeeForm.annual_leave_days}
                    onChange={e => setEmployeeForm({...employeeForm, annual_leave_days: e.target.value})}
                    placeholder="14"
                  />
                  {editingEmployee && (() => {
                    const total = Number(employeeForm.annual_leave_days) || 0;
                    const used = Number(editingEmployee.used_leave_days) || 0;
                    const remaining = Math.max(0, total - used);
                    const remColor = remaining <= 3 ? '#dc2626' : remaining <= 7 ? '#d97706' : '#16a34a';
                    return (
                      <div style={{ marginTop: '8px', padding: '10px 12px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '0.82rem' }}>
                        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: employeeLeaveHistory.paid.length || employeeLeaveHistory.unpaid.length ? '8px' : 0 }}>
                          <span style={{ color: '#475569' }}>Toplam: <strong style={{ color: '#1e293b' }}>{total} gün</strong></span>
                          <span style={{ color: '#475569' }}>Kullanılan: <strong style={{ color: '#7c3aed' }}>{used} gün</strong></span>
                          <span style={{ color: '#475569' }}>Kalan: <strong style={{ color: remColor }}>{remaining} gün</strong></span>
                        </div>
                        {employeeLeaveHistory.loading && (
                          <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>İzin geçmişi yükleniyor...</div>
                        )}
                        {!employeeLeaveHistory.loading && employeeLeaveHistory.paid.length > 0 && (
                          <div style={{ marginTop: '4px' }}>
                            <div style={{ fontWeight: 600, color: '#0d9488', marginBottom: '4px', fontSize: '0.78rem' }}>
                              Ücretli İzin Günleri ({employeeLeaveHistory.paid.length}) — izin hakkından düşer
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {employeeLeaveHistory.paid.map((p, i) => (
                                <span key={`pl-${i}`} title={p.notes || ''} style={{ padding: '2px 8px', borderRadius: '12px', background: '#ccfbf1', color: '#0f766e', fontSize: '0.74rem', fontWeight: 500 }}>
                                  {new Date(p.date).toLocaleDateString('tr-TR')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {!employeeLeaveHistory.loading && employeeLeaveHistory.unpaid.length > 0 && (
                          <div style={{ marginTop: '8px' }}>
                            <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '4px', fontSize: '0.78rem' }}>
                              Ücretsiz İzin Günleri ({employeeLeaveHistory.unpaid.length}) — maaştan kesilir
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {employeeLeaveHistory.unpaid.map((u, i) => (
                                <span key={`ul-${i}`} title={u.notes || ''} style={{ padding: '2px 8px', borderRadius: '12px', background: '#fee2e2', color: '#b91c1c', fontSize: '0.74rem', fontWeight: 500 }}>
                                  {new Date(u.date).toLocaleDateString('tr-TR')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {!employeeLeaveHistory.loading && employeeLeaveHistory.paid.length === 0 && employeeLeaveHistory.unpaid.length === 0 && (
                          <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Henüz izin kullanımı yok</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <label>Komisyon Oranı (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={employeeForm.commission_rate}
                    onChange={e => setEmployeeForm({...employeeForm, commission_rate: e.target.value})}
                    placeholder=""
                  />
                </div>
                <div className="form-group">
                  <label>Prim Formülü</label>
                  <select
                    value={employeeForm.bonus_formula_set_id || ''}
                    onChange={e => setEmployeeForm({...employeeForm, bonus_formula_set_id: e.target.value ? Number(e.target.value) : null})}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e8ecef', borderRadius: '8px', fontSize: '0.88rem' }}
                  >
                    <option value="">Formül Yok</option>
                    {bonusFormulas.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Çalışma Saatleri ve Maaş Katsayısı */}
              <div style={{ margin: '16px 0', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Clock size={16} /> Çalışma Saatleri & Maaş Katsayısı
                </h4>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '160px' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#5a6c7d', display: 'block', marginBottom: '4px' }}>30 Günlük Ücret (₺)</label>
                    <input
                      type="number"
                      step="1"
                      value={employeeForm.monthly_salary_30 || ''}
                      onChange={e => {
                        const val = e.target.value;
                        const monthly30 = parseFloat(val) || 0;
                        const whVal = parseFloat(employeeForm.work_hours_value) || 0;
                        const dailyEq = employeeForm.work_hours_unit === 'weekly' ? whVal / 7 : whVal;
                        const hourly = dailyEq > 0 && monthly30 > 0 ? (monthly30 / 30 / dailyEq) : 0;
                        setEmployeeForm({...employeeForm, monthly_salary_30: val, hourly_rate: hourly > 0 ? hourly.toFixed(2) : '', salary: monthly30 || ''});
                      }}
                      placeholder="Örn: 30000"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.88rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ flex: 1.2, minWidth: '220px' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#5a6c7d', display: 'block', marginBottom: '4px' }}>Çalışma Saati</label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        value={employeeForm.work_hours_value || ''}
                        onChange={e => {
                          const val = e.target.value;
                          const whVal = parseFloat(val) || 0;
                          const unit = employeeForm.work_hours_unit || 'daily';
                          const dailyEq = unit === 'weekly' ? whVal / 7 : whVal;
                          const monthly30 = parseFloat(employeeForm.monthly_salary_30) || 0;
                          const hourly = dailyEq > 0 && monthly30 > 0 ? (monthly30 / 30 / dailyEq) : 0;
                          setEmployeeForm({
                            ...employeeForm,
                            work_hours_value: val,
                            daily_work_hours: dailyEq > 0 ? dailyEq.toFixed(2) : '',
                            hourly_rate: hourly > 0 ? hourly.toFixed(2) : ''
                          });
                        }}
                        placeholder={employeeForm.work_hours_unit === 'weekly' ? 'Örn: 50' : 'Örn: 8'}
                        style={{ flex: 1, minWidth: 0, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.88rem', boxSizing: 'border-box' }}
                      />
                      <select
                        value={employeeForm.work_hours_unit || 'daily'}
                        onChange={e => {
                          const unit = e.target.value;
                          const whVal = parseFloat(employeeForm.work_hours_value) || 0;
                          const dailyEq = unit === 'weekly' ? whVal / 7 : whVal;
                          const monthly30 = parseFloat(employeeForm.monthly_salary_30) || 0;
                          const hourly = dailyEq > 0 && monthly30 > 0 ? (monthly30 / 30 / dailyEq) : 0;
                          setEmployeeForm({
                            ...employeeForm,
                            work_hours_unit: unit,
                            daily_work_hours: dailyEq > 0 ? dailyEq.toFixed(2) : '',
                            hourly_rate: hourly > 0 ? hourly.toFixed(2) : ''
                          });
                        }}
                        style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.88rem', background: '#fff', cursor: 'pointer' }}
                      >
                        <option value="daily">Günlük</option>
                        <option value="weekly">Haftalık</option>
                      </select>
                    </div>
                  </div>
                  {(() => {
                    const hr = parseFloat(employeeForm.hourly_rate) || 0;
                    return hr > 0 ? (
                      <div style={{ flex: 1, minWidth: '120px', display: 'flex', alignItems: 'flex-end' }}>
                        <div style={{ width: '100%', padding: '8px 12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '2px' }}>Hesaplanan Saatlik Ücret</div>
                          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0d9488' }}>₺{hr.toFixed(2)}</div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                  <div style={{ flex: 1, minWidth: '130px' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#5a6c7d', display: 'block', marginBottom: '4px' }}>Mesai Çarpanı</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={employeeForm.overtime_multiplier ?? ''}
                      onChange={e => setEmployeeForm({...employeeForm, overtime_multiplier: e.target.value})}
                      placeholder="1.5"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.88rem', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                {/* Haftalık program tablosu */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: '#e2e8f0' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderRadius: '6px 0 0 0' }}>Gün</th>
                        <th style={{ padding: '6px 8px', textAlign: 'center' }}>Çalışıyor</th>
                        <th style={{ padding: '6px 8px', textAlign: 'center' }}>Başlangıç</th>
                        <th style={{ padding: '6px 8px', textAlign: 'center' }}>Bitiş</th>
                        <th style={{ padding: '6px 8px', textAlign: 'center', borderRadius: '0 6px 0 0' }}>Saat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: 'monday', label: 'Pazartesi' },
                        { key: 'tuesday', label: 'Salı' },
                        { key: 'wednesday', label: 'Çarşamba' },
                        { key: 'thursday', label: 'Perşembe' },
                        { key: 'friday', label: 'Cuma' },
                        { key: 'saturday', label: 'Cumartesi' },
                        { key: 'sunday', label: 'Pazar' }
                      ].map(day => {
                        const schedule = employeeForm.work_schedule?.[day.key] || { active: false, start: '', end: '' };
                        let dailyHours = 0;
                        if (schedule.active && schedule.start && schedule.end) {
                          const [sh, sm] = schedule.start.split(':').map(Number);
                          const [eh, em] = schedule.end.split(':').map(Number);
                          dailyHours = (eh + em / 60) - (sh + sm / 60);
                          if (dailyHours < 0) dailyHours = 0;
                        }
                        return (
                          <tr key={day.key} style={{ borderBottom: '1px solid #e2e8f0', opacity: schedule.active ? 1 : 0.5 }}>
                            <td style={{ padding: '6px 8px', fontWeight: 600 }}>{day.label}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={schedule.active}
                                onChange={e => {
                                  const newSchedule = { ...employeeForm.work_schedule, [day.key]: { ...schedule, active: e.target.checked } };
                                  setEmployeeForm({ ...employeeForm, work_schedule: newSchedule });
                                }}
                                style={{ width: '16px', height: '16px', accentColor: '#00bfa6', cursor: 'pointer' }}
                              />
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                              <input
                                type="time"
                                value={schedule.start || ''}
                                disabled={!schedule.active}
                                onChange={e => {
                                  const newSchedule = { ...employeeForm.work_schedule, [day.key]: { ...schedule, start: e.target.value } };
                                  setEmployeeForm({ ...employeeForm, work_schedule: newSchedule });
                                }}
                                style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.82rem', width: '90px' }}
                              />
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                              <input
                                type="time"
                                value={schedule.end || ''}
                                disabled={!schedule.active}
                                onChange={e => {
                                  const newSchedule = { ...employeeForm.work_schedule, [day.key]: { ...schedule, end: e.target.value } };
                                  setEmployeeForm({ ...employeeForm, work_schedule: newSchedule });
                                }}
                                style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.82rem', width: '90px' }}
                              />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, color: dailyHours > 0 ? '#0d9488' : '#94a3b8' }}>
                              {dailyHours > 0 ? `${dailyHours.toFixed(1)}s` : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Özet bilgiler */}
                {(() => {
                  const calc = calculateFromSchedule(employeeForm.work_schedule, employeeForm.hourly_rate);
                  return (
                    <div style={{ display: 'flex', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '100px', padding: '8px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '2px' }}>Haftalık</div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>{calc.weeklyHours.toFixed(1)} saat</div>
                      </div>
                      <div style={{ flex: 1, minWidth: '100px', padding: '8px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '2px' }}>Aylık (ort.)</div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>{calc.monthlyHours.toFixed(0)} saat</div>
                      </div>
                    </div>
                  );
                })()}

                {parseFloat(employeeForm.hourly_rate) > 0 && (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', fontSize: '0.75rem', color: '#1e40af' }}>
                    Devamsızlık: gün başına ortalama saat x saatlik ücret kesilir | Geç kalma: her geç kalma 1 saatlik ücret kesilir | Mesai: {parseFloat(employeeForm.overtime_multiplier) || 1.5}x saatlik ücret eklenir
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '12px 0', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#5a6c7d' }}>Mağazalar:</span>
                {stores.map(store => {
                  const isChecked = employeeForm.store_ids?.includes(store.id);
                  return (
                    <label key={store.id} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px 10px', borderRadius: '6px', border: `1.5px solid ${isChecked ? '#00bfa6' : '#e2e8f0'}`, background: isChecked ? 'rgba(0,191,166,0.08)' : '#fff', transition: 'all 0.2s' }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          const newStoreIds = e.target.checked
                            ? [...(employeeForm.store_ids || []), store.id]
                            : (employeeForm.store_ids || []).filter(id => id !== store.id);
                          setEmployeeForm({...employeeForm, store_ids: newStoreIds});
                        }}
                        style={{ width: '14px', height: '14px', accentColor: '#009688' }}
                      />
                      <span style={{ fontSize: '0.8rem', fontWeight: 500, color: isChecked ? '#0d9488' : '#64748b' }}>{store.name}</span>
                    </label>
                  );
                })}
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#5a6c7d', marginBottom: '4px', display: 'block' }}>Notlar</label>
                <textarea
                  value={employeeForm.notes}
                  onChange={e => setEmployeeForm({...employeeForm, notes: e.target.value})}
                  placeholder="Ek notlar..."
                  rows="2"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #e8ecef', borderRadius: '8px', fontSize: '0.88rem', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => handleCloseModal('employee', setShowEmployeeModal)}>İptal</button>
              <button className="btn-save" onClick={handleSaveEmployee}>
                {editingEmployee ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAAŞ DETAY MODAL */}
      {showSalaryDetailModal && selectedEmployeeSalary && (
        <div className={`modal-overlay ${closingModal === 'salary' ? 'closing' : ''}`}>
          <div className="modal large" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>Maaş Bordrosu - {selectedEmployeeSalary.employee?.name}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('salary', setShowSalaryDetailModal)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <div className="salary-detail">
                {/* Dönem Navigasyon */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
                  <button
                    onClick={() => {
                      let m = Number(selectedEmployeeSalary.period?.month) - 1;
                      let y = Number(selectedEmployeeSalary.period?.year);
                      if (m < 1) { m = 12; y--; }
                      viewSalaryDetail(selectedEmployeeSalary.employee, m, y);
                    }}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '1rem', fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center' }}
                    title="Önceki ay"
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <div className="salary-period" style={{ margin: 0, minWidth: '160px', textAlign: 'center' }}>
                    {monthNames[selectedEmployeeSalary.period?.month - 1]} {selectedEmployeeSalary.period?.year}
                  </div>
                  <button
                    onClick={() => {
                      let m = Number(selectedEmployeeSalary.period?.month) + 1;
                      let y = Number(selectedEmployeeSalary.period?.year);
                      if (m > 12) { m = 1; y++; }
                      viewSalaryDetail(selectedEmployeeSalary.employee, m, y);
                    }}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '1rem', fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', transform: 'rotate(180deg)' }}
                    title="Sonraki ay"
                  >
                    <ArrowLeft size={16} />
                  </button>
                </div>

                {/* Maaş Özeti */}
                <div className="salary-breakdown">
                  <div className="breakdown-row">
                    <span>Temel Maaş {selectedEmployeeSalary.hourly_rate > 0 ? `(₺${Number(selectedEmployeeSalary.hourly_rate).toFixed(2)}/saat x ${Number(selectedEmployeeSalary.total_monthly_hours || 0).toFixed(0)}s)` : ''}</span>
                    <span>₺{Number(selectedEmployeeSalary.base_salary || 0).toLocaleString()}</span>
                  </div>
                  {selectedEmployeeSalary.commission_amount > 0 && (
                    <div className="breakdown-row success">
                      <span>Komisyon + Sabit Prim (₺{Number(selectedEmployeeSalary.fixed_bonus || 0).toLocaleString()})</span>
                      <span style={{ color: '#16a34a' }}>+₺{Number(selectedEmployeeSalary.commission_amount || 0).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedEmployeeSalary.overtime_hours > 0 && (
                    <div className="breakdown-row success">
                      <span>Ek Mesai ({selectedEmployeeSalary.overtime_hours} saat x {selectedEmployeeSalary.overtime_multiplier || 1.5})</span>
                      <span style={{ color: '#16a34a' }}>+₺{Number(selectedEmployeeSalary.overtime_amount || 0).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedEmployeeSalary.absent_days > 0 && (
                    <div className="breakdown-row danger">
                      <span>Devamsızlık Kesintisi ({selectedEmployeeSalary.absent_days} gün)</span>
                      <span style={{ color: '#dc2626' }}>-₺{Number((selectedEmployeeSalary.deductions || 0) - (selectedEmployeeSalary.late_deduction || 0) - (selectedEmployeeSalary.hourly_absence_deduction || 0)).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedEmployeeSalary.hourly_absence_hours > 0 && (
                    <div className="breakdown-row danger">
                      <span>Saatlik Devamsızlık ({Number(selectedEmployeeSalary.hourly_absence_hours).toFixed(2).replace(/\.?0+$/, '')} saat)</span>
                      <span style={{ color: '#dc2626' }}>-₺{Number(selectedEmployeeSalary.hourly_absence_deduction || 0).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedEmployeeSalary.late_days > 0 && (
                    <div className="breakdown-row danger">
                      <span>Geç Kalma Kesintisi ({selectedEmployeeSalary.late_days} gün)</span>
                      <span style={{ color: '#dc2626' }}>-₺{Number(selectedEmployeeSalary.late_deduction || 0).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedEmployeeSalary.paid_leave_days > 0 && (
                    <div className="breakdown-row highlight">
                      <span>Ücretli İzin ({selectedEmployeeSalary.paid_leave_days} gün)</span>
                      <span style={{ color: '#0d9488' }}>İzin hakkından düşüldü</span>
                    </div>
                  )}
                  {(() => {
                    const details = selectedEmployeeSalary.formula_bonus_details || [];
                    if (details.length === 0) return null;
                    const formulaName = details.find(d => d.name)?.name || '';
                    const storeItems = details.filter(d => d.type === 'store');
                    const empItems = details.filter(d => d.type === 'employee');
                    const storeTotal = storeItems.filter(d => d.met).reduce((s, d) => s + (Number(d.bonus_amount) || 0), 0);
                    const empTotal = empItems.filter(d => d.met).reduce((s, d) => s + (Number(d.bonus_amount) || 0), 0);
                    const storeMet = storeTotal > 0;
                    const empMet = empTotal > 0;
                    const notMetDetail = [...storeItems, ...empItems].find(d => !d.met) || {};
                    const storeRevMet = storeMet || !!notMetDetail.store_revenue_target_met;
                    const empThresholdMet = empMet || !!notMetDetail.employee_target_met;
                    return (
                      <>
                        {storeItems.length > 0 && (
                          <div className={`breakdown-row ${storeMet ? 'success' : ''}`}>
                            <span>
                              {formulaName} - Mağaza Hedef Primi
                              {!storeMet && storeRevMet && !empThresholdMet && <span style={{ fontSize: '0.72rem', color: '#dc2626', marginLeft: '4px' }}>(kişisel hedef aşılmadığı için ödenmedi)</span>}
                              {!storeMet && !storeRevMet && <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: '4px' }}>(hedef aşılmadı)</span>}
                            </span>
                            <span style={{ color: storeMet ? '#16a34a' : '#94a3b8', fontWeight: storeMet ? 600 : 400 }}>
                              {storeMet ? `+₺${storeTotal.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}` : '-'}
                            </span>
                          </div>
                        )}
                        {empItems.length > 0 && (
                          <div className={`breakdown-row ${empMet ? 'success' : ''}`}>
                            <span>
                              {formulaName} - Kişisel Hedef Primi
                              {!empMet && empThresholdMet && !storeRevMet && <span style={{ fontSize: '0.72rem', color: '#dc2626', marginLeft: '4px' }}>(mağaza hedefi aşılmadığı için ödenmedi)</span>}
                              {!empMet && !empThresholdMet && <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: '4px' }}>(hedef aşılmadı)</span>}
                            </span>
                            <span style={{ color: empMet ? '#16a34a' : '#94a3b8', fontWeight: empMet ? 600 : 400 }}>
                              {empMet ? `+₺${empTotal.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}` : '-'}
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <div className="breakdown-row total">
                    <span>NET ÖDEME</span>
                    <span>₺{Number(selectedEmployeeSalary.total_amount || 0).toLocaleString()}</span>
                  </div>
                </div>

                {/* Aylık Maaş & Prim Ödeme Durumu */}
                {(() => {
                  const totalBonus = (Number(selectedEmployeeSalary.commission_amount) || 0) + (Number(selectedEmployeeSalary.formula_bonus_amount) || 0);
                  const totalAmount = Number(selectedEmployeeSalary.total_amount) || 0;
                  const salaryAmount = Math.max(0, totalAmount - totalBonus);
                  const noBonus = totalBonus <= 0.0001;
                  const noSalary = salaryAmount <= 0.0001;
                  const periodLabel = `${monthNames[selectedEmployeeSalary.period?.month - 1]} ${selectedEmployeeSalary.period?.year}`;
                  return (
                    <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                      {/* Maaş Kartı */}
                      <div style={{
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        background: noSalary ? '#fafafa' : (currentSalaryStatus.paid ? '#f0fdf4' : '#fffbeb')
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
                          <div>
                            <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
                              {periodLabel} Maaşı
                            </div>
                            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: noSalary ? '#94a3b8' : '#1e40af', marginTop: '2px' }}>
                              ₺{salaryAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </div>
                            {currentSalaryStatus.paid && currentSalaryStatus.paid_at && (
                              <div style={{ fontSize: '0.7rem', color: '#16a34a', marginTop: '2px' }}>
                                Ödendi: {new Date(currentSalaryStatus.paid_at).toLocaleString('tr-TR')}
                              </div>
                            )}
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: noSalary ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600, color: noSalary ? '#9ca3af' : '#1e293b', whiteSpace: 'nowrap' }}>
                            <input
                              type="checkbox"
                              checked={currentSalaryStatus.paid}
                              onChange={toggleCurrentSalaryPaid}
                              disabled={noSalary || currentSalaryStatus.loading}
                              style={{ width: '18px', height: '18px', cursor: noSalary ? 'not-allowed' : 'pointer' }}
                            />
                            Ödendi
                          </label>
                        </div>
                        <textarea
                          value={salaryNotesDraft}
                          onChange={(e) => setSalaryNotesDraft(e.target.value)}
                          onBlur={saveSalaryNotes}
                          placeholder="Maaş notu (örn: nakit ödendi, banka havalesi, kesinti açıklaması...)"
                          rows={2}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.82rem',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            background: '#fff',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      {/* Prim Kartı */}
                      <div style={{
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        background: noBonus ? '#fafafa' : (currentBonusStatus.paid ? '#f0fdf4' : '#fffbeb')
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
                          <div>
                            <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
                              {periodLabel} Primi
                            </div>
                            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: noBonus ? '#94a3b8' : '#0d9488', marginTop: '2px' }}>
                              ₺{totalBonus.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </div>
                            {currentBonusStatus.paid && currentBonusStatus.paid_at && (
                              <div style={{ fontSize: '0.7rem', color: '#16a34a', marginTop: '2px' }}>
                                Ödendi: {new Date(currentBonusStatus.paid_at).toLocaleString('tr-TR')}
                              </div>
                            )}
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: noBonus ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600, color: noBonus ? '#9ca3af' : '#1e293b', whiteSpace: 'nowrap' }}>
                            <input
                              type="checkbox"
                              checked={currentBonusStatus.paid}
                              onChange={toggleCurrentBonusPaid}
                              disabled={noBonus || currentBonusStatus.loading}
                              style={{ width: '18px', height: '18px', cursor: noBonus ? 'not-allowed' : 'pointer' }}
                            />
                            Ödendi
                          </label>
                        </div>
                        <textarea
                          value={bonusNotesDraft}
                          onChange={(e) => setBonusNotesDraft(e.target.value)}
                          onBlur={saveBonusNotes}
                          placeholder="Prim notu (örn: nakit ödendi, banka havalesi, kesinti açıklaması...)"
                          rows={2}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.82rem',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            background: '#fff',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* Aylık Hareket Detayı - Tüm Günler */}
                <div style={{ marginTop: '20px' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>
                    Aylık Hareket Detayı
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: '#f1f5f9' }}>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Tarih</th>
                          <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Durum</th>
                          <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Program</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Açıklama</th>
                          <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Etki</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const sal = selectedEmployeeSalary;
                          const m = sal.period?.month;
                          const y = sal.period?.year;
                          const daysInMonth = new Date(y, m, 0).getDate();
                          const dayKeyMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                          const dayLabelMap = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
                          const ws = sal.work_schedule || {};
                          const attMap = {};
                          (sal.attendance_records || []).forEach(r => {
                            const d = new Date(r.date).getDate();
                            attMap[d] = r;
                          });
                          const otMap = {};
                          (sal.overtime_records || []).forEach(r => {
                            const d = new Date(r.date).getDate();
                            otMap[d] = r;
                          });
                          const hr = sal.hourly_rate || 0;
                          const avgH = sal.avg_daily_hours || 0;
                          const today = new Date();
                          const isCurrentMonth = today.getMonth() + 1 === Number(m) && today.getFullYear() === Number(y);
                          const todayDate = today.getDate();

                          const statusMap = {
                            present: { label: 'Geldi', color: '#16a34a', bg: '#f0fdf4' },
                            absent: { label: 'Gelmedi', color: '#dc2626', bg: '#fef2f2' },
                            late: { label: 'Geç Kaldı', color: '#d97706', bg: '#fffbeb' },
                            half_day: { label: 'Yarım Gün', color: '#2563eb', bg: '#eff6ff' },
                            paid_leave: { label: 'Ücretli İzin', color: '#0d9488', bg: '#f0fdfa' },
                            unpaid_leave: { label: 'Ücretsiz İzin', color: '#dc2626', bg: '#fef2f2' },
                            sick_leave: { label: 'Raporlu', color: '#7c3aed', bg: '#f5f3ff' }
                          };

                          const rows = [];
                          for (let d = 1; d <= daysInMonth; d++) {
                            const dateObj = new Date(y, m - 1, d);
                            const dow = dateObj.getDay();
                            const dayKey = dayKeyMap[dow];
                            const dayLabel = dayLabelMap[dow];
                            const schedule = ws[dayKey];
                            const isWorkDay = schedule?.active;
                            const attRecord = attMap[d];
                            const otRecord = otMap[d];
                            const isFuture = isCurrentMonth && d > todayDate;

                            // Programdaki saat aralığı
                            let scheduleText = '-';
                            let scheduledHours = 0;
                            if (isWorkDay && schedule.start && schedule.end) {
                              scheduleText = `${schedule.start} - ${schedule.end}`;
                              const [sh, sm] = schedule.start.split(':').map(Number);
                              const [eh, em] = schedule.end.split(':').map(Number);
                              scheduledHours = (eh + em / 60) - (sh + sm / 60);
                            }

                            // Durum ve renk belirle
                            let st, bg, impact = '', impactColor = '#64748b';
                            if (isFuture) {
                              st = { label: isWorkDay ? 'Planlandı' : 'Tatil', color: '#94a3b8' };
                              bg = '#f8fafc';
                            } else if (attRecord) {
                              st = statusMap[attRecord.status] || { label: attRecord.status, color: '#64748b' };
                              bg = st.bg || '#f8fafc';
                              if (attRecord.status === 'absent' || attRecord.status === 'unpaid_leave') {
                                const ded = hr > 0 ? avgH * hr : (Number(sal.base_salary) / 30);
                                impact = `-₺${Math.round(ded).toLocaleString()}`;
                                impactColor = '#dc2626';
                              } else if (attRecord.status === 'half_day') {
                                const ded = hr > 0 ? (avgH * hr * 0.5) : (Number(sal.base_salary) / 60);
                                impact = `-₺${Math.round(ded).toLocaleString()}`;
                                impactColor = '#dc2626';
                              } else if (attRecord.status === 'late') {
                                if (hr > 0) { impact = `-₺${Math.round(hr).toLocaleString()}`; impactColor = '#d97706'; }
                              } else if (attRecord.status === 'paid_leave') {
                                impact = 'İzin hakkından';
                                impactColor = '#0d9488';
                              }
                            } else if (!isWorkDay) {
                              st = { label: 'Tatil', color: '#94a3b8' };
                              bg = '#f8fafc';
                            } else {
                              st = { label: 'Geldi', color: '#16a34a' };
                              bg = '#fff';
                            }

                            // Mesai varsa ek satır eklenecek ama önce ana satırı ekle
                            const dateStr = `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}`;
                            rows.push(
                              <tr key={`day-${d}`} style={{ borderBottom: '1px solid #f1f5f9', background: bg, opacity: isFuture ? 0.45 : 1 }}>
                                <td style={{ padding: '6px 10px', fontWeight: 500 }}>
                                  {dateStr}
                                  <span style={{ color: '#94a3b8', marginLeft: '4px', fontSize: '0.75rem' }}>{dayLabel}</span>
                                </td>
                                <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                  <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.73rem', fontWeight: 600, color: st.color, background: 'rgba(255,255,255,0.7)', border: `1px solid ${st.color}30` }}>
                                    {st.label}
                                  </span>
                                </td>
                                <td style={{ padding: '6px 10px', textAlign: 'center', fontSize: '0.76rem', color: '#475569' }}>
                                  {attRecord?.check_in || attRecord?.check_out
                                    ? `${(attRecord.check_in || '').substring(0, 5)} / ${(attRecord.check_out || '').substring(0, 5)}`
                                    : (isWorkDay ? scheduleText : '-')}
                                </td>
                                <td style={{ padding: '6px 10px', fontSize: '0.76rem', color: '#64748b', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {attRecord?.notes || (!isWorkDay ? 'Çalışma günü değil' : '')}
                                </td>
                                <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: impactColor, fontSize: '0.8rem' }}>
                                  {impact || '-'}
                                </td>
                              </tr>
                            );

                            // Mesai kaydı varsa ek satır
                            if (otRecord) {
                              const otAmount = hr > 0 ? otRecord.hours * hr * 1.5 : 0;
                              rows.push(
                                <tr key={`ot-${d}`} style={{ borderBottom: '1px solid #f1f5f9', background: '#f0fdf4' }}>
                                  <td style={{ padding: '4px 10px 4px 24px', fontSize: '0.76rem', color: '#0d9488' }}></td>
                                  <td style={{ padding: '4px 10px', textAlign: 'center' }}>
                                    <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.73rem', fontWeight: 600, color: '#0d9488', background: '#ecfdf5', border: '1px solid #a7f3d0' }}>
                                      Ek Mesai
                                    </span>
                                  </td>
                                  <td style={{ padding: '4px 10px', textAlign: 'center', fontSize: '0.76rem', color: '#0d9488', fontWeight: 600 }}>
                                    {otRecord.hours} saat
                                  </td>
                                  <td style={{ padding: '4px 10px', fontSize: '0.76rem', color: '#64748b' }}>
                                    {otRecord.notes || ''}
                                  </td>
                                  <td style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 600, color: '#16a34a', fontSize: '0.8rem' }}>
                                    {otAmount > 0 ? `+₺${Math.round(otAmount).toLocaleString()}` : '-'}
                                  </td>
                                </tr>
                              );
                            }
                          }
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => handleCloseModal('salary', setShowSalaryDetailModal)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* YOKLAMA KAYDI MODAL */}
      {showAttendanceModal && selectedAttendanceEmployee && (
        <div className={`modal-overlay ${closingModal === 'attendance' ? 'closing' : ''}`}>
          <div className="modal attendance-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yoklama Kaydı - {selectedAttendanceEmployee.name}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('attendance', setShowAttendanceModal)}>✕</button>
            </div>
            <div className="modal-body">
              {/* Yeni Kayıt Formu */}
              <div className="attendance-form-section">
                <h4>Yeni Kayıt Ekle</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Tarih *</label>
                    <input
                      type="date"
                      value={attendanceForm.date}
                      onChange={e => setAttendanceForm({...attendanceForm, date: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Durum *</label>
                    <select
                      value={attendanceForm.status}
                      onChange={e => setAttendanceForm({...attendanceForm, status: e.target.value})}
                    >
                      <option value="unpaid_leave">Ücretsiz İzin (maaştan kesilir)</option>
                      <option value="hourly_absence">Saatlik Devamsızlık (maaştan kesilir)</option>
                      <option value="paid_leave">Ücretli İzin (izin hakkından düşer)</option>
                    </select>
                  </div>
                </div>
                {attendanceForm.status === 'hourly_absence' && (
                  <div className="form-group">
                    <label>Kaç saat? *</label>
                    <input
                      type="number"
                      step="0.25"
                      min="0.25"
                      max="24"
                      value={attendanceForm.partial_hours}
                      onChange={e => setAttendanceForm({...attendanceForm, partial_hours: e.target.value})}
                      placeholder="Örn: 1 (erken çıktığı süre)"
                    />
                    <small style={{ display: 'block', marginTop: '4px', color: '#64748b', fontSize: '0.75rem' }}>
                      Bu saat sayısı × saatlik ücret kadar maaştan kesilir
                    </small>
                  </div>
                )}
                <div className="form-group">
                  <label>Sebep / Açıklama</label>
                  <textarea
                    value={attendanceForm.reason}
                    onChange={e => setAttendanceForm({...attendanceForm, reason: e.target.value})}
                    placeholder="Örn: Hastalık nedeniyle gelmedi, 1 saat erken çıktı..."
                    rows="2"
                  />
                </div>
                <button className="btn-save" onClick={handleSaveAttendanceRecord}>
                  Kaydı Ekle
                </button>
              </div>

              {/* Geçmiş Kayıtlar */}
              <div className="attendance-history-section">
                <h4>Geçmiş Yoklama Kayıtları</h4>
                {attendanceHistory.length === 0 ? (
                  <div className="empty-state small">
                    <p>Henüz yoklama kaydı yok</p>
                  </div>
                ) : (
                  <div className="history-list">
                    {attendanceHistory.map((record, index) => {
                      const statusInfoMap = {
                        present: { label: 'Geldi', color: '#16a34a' },
                        absent: { label: 'Gelmedi', color: '#dc2626' },
                        late: { label: 'Geç Kaldı', color: '#f59e0b' },
                        half_day: { label: 'Yarım Gün', color: '#ca8a04' },
                        paid_leave: { label: 'Ücretli İzin', color: '#0d9488' },
                        unpaid_leave: { label: 'Ücretsiz İzin', color: '#dc2626' },
                        sick_leave: { label: 'Raporlu', color: '#7c3aed' },
                        hourly_absence: {
                          label: record.partial_hours
                            ? `Saatlik Devamsızlık (${Number(record.partial_hours).toFixed(2).replace(/\.?0+$/, '')}sa)`
                            : 'Saatlik Devamsızlık',
                          color: '#dc2626'
                        }
                      };
                      const info = statusInfoMap[record.status];
                      return (
                      <div key={index} className={`history-item ${record.status}`}>
                        <div className="history-date">
                          {new Date(record.date).toLocaleDateString('tr-TR', {
                            day: '2-digit', month: 'long', year: 'numeric', weekday: 'long'
                          })}
                          {info && <span style={{ marginLeft: '6px', color: info.color, fontWeight: 600 }}>({info.label})</span>}
                        </div>
                        {record.notes && (
                          <div className="history-notes">
                            {record.notes}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteAttendance(record)}
                          title="Kaydı sil"
                          style={{ marginLeft: 'auto', marginRight: '12px', padding: '6px 8px', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => handleCloseModal('attendance', setShowAttendanceModal)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* EK MESAİ MODAL */}
      {showOvertimeModal && selectedOvertimeEmployee && (
        <div className={`modal-overlay ${closingModal === 'overtime' ? 'closing' : ''}`}>
          <div className="modal overtime-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ek Mesai - {selectedOvertimeEmployee.name}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('overtime', setShowOvertimeModal)}>✕</button>
            </div>
            <div className="modal-body">
              {/* Yeni Kayıt Formu */}
              <div className="overtime-form-section">
                <h4>Yeni Ek Mesai Ekle</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Tarih *</label>
                    <input
                      type="date"
                      value={overtimeForm.date}
                      onChange={e => setOvertimeForm({...overtimeForm, date: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Saat *</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      max="12"
                      value={overtimeForm.hours}
                      onChange={e => setOvertimeForm({...overtimeForm, hours: e.target.value})}
                      placeholder="Örn: 2.5"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Açıklama</label>
                  <textarea
                    value={overtimeForm.notes}
                    onChange={e => setOvertimeForm({...overtimeForm, notes: e.target.value})}
                    placeholder="Örn: Yoğunluk nedeniyle fazla mesai, Özel sipariş için..."
                    rows="2"
                  />
                </div>
                <button className="btn-save" onClick={handleSaveOvertime}>
                  Mesai Ekle
                </button>
              </div>

              {/* Geçmiş Kayıtlar */}
              <div className="overtime-history-section">
                <h4>Ek Mesai Geçmişi</h4>
                {overtimeHistory.length === 0 ? (
                  <div className="empty-state small">
                    <p>Henüz ek mesai kaydı yok</p>
                  </div>
                ) : (
                  <div className="history-list">
                    {overtimeHistory.map((record, index) => (
                      <div key={index} className="history-item overtime">
                        <div className="history-date">
                          {new Date(record.date).toLocaleDateString('tr-TR', {
                            day: '2-digit', month: 'long', year: 'numeric', weekday: 'long'
                          })}
                        </div>
                        <div className="history-hours">
                          <span className="hours-badge">{record.hours} saat</span>
                        </div>
                        {record.notes && (
                          <div className="history-notes">
                            {record.notes}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteOvertime(record)}
                          title="Kaydı sil"
                          style={{ marginLeft: 'auto', padding: '6px 8px', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => handleCloseModal('overtime', setShowOvertimeModal)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Log Modal */}
      <LogModal
        isOpen={showLogModal}
        onClose={() => handleCloseModal('log', setShowLogModal)}
        module="personnel"
        title="Personel Yönetimi"
        showEntityCode={false}
      />

      {/* SATIŞ GEÇMİŞİ MODAL */}
      {showSalesHistoryModal && salesHistoryEmployee && (
        <div className={`modal-overlay ${closingModal === 'salesHistory' ? 'closing' : ''}`}>
          <div className="modal large" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2>Satış Geçmişi - {salesHistoryEmployee.name}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('salesHistory', setShowSalesHistoryModal)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto', padding: '16px' }}>
              <div style={{ marginBottom: '12px', fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                {monthNames[selectedMonth - 1]} {selectedYear} - {salesHistoryData.length} kayıt
              </div>
              {loadingSalesHistory ? (
                <div className="loading">Yükleniyor...</div>
              ) : salesHistoryData.length === 0 ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>Bu dönemde satış kaydı bulunamadı</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Tarih</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Fiş No</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Tür</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Müşteri</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Ödeme</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Tutar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesHistoryData.map((sale, idx) => {
                        const isRefund = sale.sale_type === 'refund' || sale.status === 'refunded';
                        const isExchangePart = !!sale._exchangeRole; // 'refund' veya 'new'
                        const t = isRefund
                          ? { label: isExchangePart ? 'İade (Değişim)' : 'İade', color: '#dc2626', bg: '#fef2f2' }
                          : { label: isExchangePart ? 'Satış (Değişim)' : 'Satış', color: '#16a34a', bg: '#f0fdf4' };
                        const amount = getSalePaidAmount(sale);
                        const cashierCount = sale._cashierCount || 1;
                        const isShared = cashierCount > 1;
                        const sharedAmount = amount / cashierCount;
                        const showNegative = amount < 0;
                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: t.bg }}>
                            <td style={{ padding: '7px 10px' }}>
                              {new Date(sale.created_at).toLocaleDateString('tr-TR')}
                              <span style={{ color: '#94a3b8', marginLeft: '4px', fontSize: '0.75rem' }}>
                                {new Date(sale.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </td>
                            <td style={{ padding: '7px 10px', fontWeight: 500 }}>
                              {sale.receipt_number || sale.store_sale_number || sale.id}
                              {isExchangePart && (
                                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '2px' }}>
                                  Değişim Fişi
                                </div>
                              )}
                              {isShared && (
                                <div style={{ fontSize: '0.7rem', color: '#7c3aed', marginTop: '2px', fontWeight: 600 }}>
                                  {cashierCount} kasiyer paylaştı
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '7px 10px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.73rem', fontWeight: 600, color: t.color, background: 'rgba(255,255,255,0.7)', border: `1px solid ${t.color}30` }}>
                                {t.label}
                              </span>
                            </td>
                            <td style={{ padding: '7px 10px', color: '#475569' }}>{sale.customer_name || 'Genel Müşteri'}</td>
                            <td style={{ padding: '7px 10px' }}>
                              <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '0.73rem', background: '#f1f5f9', color: '#475569' }}>
                                {sale.payment_method || '-'}
                              </span>
                            </td>
                            <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: showNegative ? '#dc2626' : '#1e293b' }}>
                              {isShared ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                  <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500 }}>
                                    {showNegative ? '-' : ''}₺{Math.abs(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                  </span>
                                  <span title={`${cashierCount} kasiyere bölündü`}>
                                    {showNegative ? '-' : ''}₺{Math.abs(sharedAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                    <small style={{ color: '#7c3aed', marginLeft: '4px', fontWeight: 600 }}>÷{cashierCount}</small>
                                  </span>
                                </div>
                              ) : (
                                <>{showNegative ? '-' : ''}₺{Math.abs(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
                        <td colSpan={5} style={{ padding: '10px', textAlign: 'right', fontSize: '0.85rem' }}
                            title="Personel sayfasındaki 'Net Satış' sütunu ile aynı: değişim satırları farkıyla, paylaşımlı satışlar bölünmüş, statü='refunded' satırlar hariç.">
                          Prime Dahil Toplam:
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right', fontSize: '0.9rem', color: '#7c3aed' }}>
                          ₺{salesHistoryData.reduce((sum, s) => {
                            if (s.status === 'refunded') return sum; // backend bonus_eligible bunları hariç tutar
                            const cnt = s._cashierCount || 1;
                            // Tüm girdiler doğal şekilde toplanır:
                            //  • İade (sale_type='refund'): getSalePaidAmount negatif döner → çıkarır
                            //  • Normal satış: getSalePaidAmount (scrap_total_amount dahil)
                            //  • Değişimde: iade (-) + yeni satış (+) ayrı ayrı eklenir → net etki = fark
                            //  • Çoklu kasiyer: cnt ile bölünür
                            return sum + getSalePaidAmount(s) / cnt;
                          }, 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => handleCloseModal('salesHistory', setShowSalesHistoryModal)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Genel Onay Modal (window.confirm yerine) */}
      {showConfirmModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                         confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                         'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{confirmModalConfig.title}</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px', background: '#fff' }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#374151', lineHeight: '1.6' }}>
                {confirmModalConfig.message}
              </p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.cancelText}
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  confirmModalConfig.onConfirm();
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                             confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                             'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonnelManagement;
